// src/pages/FundamentalAnalysisPage.js
// FullMetrix — Dashboard fondamental compact, sans menu interne.

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Activity,
  AlertTriangle,
  ArrowDown,
  ArrowRight,
  ArrowUp,
  BarChart3,
  Bell,
  CalendarDays,
  CheckCircle2,
  ChevronDown,
  CircleDollarSign,
  Clock3,
  CloudOff,
  Eye,
  Gauge,
  Globe2,
  Database,
  Layers3,
  RefreshCw,
  Search,
  ShieldAlert,
  ShieldCheck,
  Sparkles,
  Target,
  TrendingDown,
  TrendingUp,
  X,
  Zap,
} from "lucide-react";

import {
  apiGetFundamentalDashboard,
} from "../api/api";
import { motion } from "framer-motion";

const FLAGS = {
  USD: "🇺🇸",
  EUR: "🇪🇺",
  GBP: "🇬🇧",
  JPY: "🇯🇵",
  CHF: "🇨🇭",
  CAD: "🇨🇦",
  AUD: "🇦🇺",
  NZD: "🇳🇿",
};

const CURRENCY_MARKS = {
  USD: "US",
  EUR: "EU",
  GBP: "GB",
  JPY: "JP",
  CHF: "CH",
  CAD: "CA",
  AUD: "AU",
  NZD: "NZ",
};

const TIME_ZONE_STORAGE_KEY = "fullmetrix_fundamental_timezone_v1";
const DEFAULT_TIME_ZONE = "UTC";
const COMMON_TIME_ZONES = [
  "UTC",
  "Africa/Lome",
  "Africa/Abidjan",
  "Africa/Accra",
  "Africa/Lagos",
  "Africa/Dakar",
  "Africa/Casablanca",
  "Africa/Algiers",
  "Africa/Tunis",
  "Africa/Cairo",
  "Africa/Johannesburg",
  "Europe/London",
  "Europe/Paris",
  "Europe/Brussels",
  "Europe/Zurich",
  "America/New_York",
  "America/Toronto",
  "America/Chicago",
  "America/Los_Angeles",
  "Asia/Dubai",
  "Asia/Tokyo",
  "Asia/Singapore",
  "Australia/Sydney",
];

function detectBrowserTimeZone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || DEFAULT_TIME_ZONE;
  } catch {
    return DEFAULT_TIME_ZONE;
  }
}

function isValidTimeZone(value) {
  if (!value) return false;
  try {
    new Intl.DateTimeFormat("fr-FR", { timeZone: value }).format(new Date());
    return true;
  } catch {
    return false;
  }
}

function getInitialTimeZone() {
  if (typeof window !== "undefined") {
    try {
      const stored = window.localStorage.getItem(TIME_ZONE_STORAGE_KEY);
      if (isValidTimeZone(stored)) return stored;
    } catch {
      // Le stockage local peut être désactivé : le fuseau détecté reste utilisable.
    }
  }
  const detected = detectBrowserTimeZone();
  return isValidTimeZone(detected) ? detected : DEFAULT_TIME_ZONE;
}

let activeTimeZone = getInitialTimeZone();

function setFormattingTimeZone(value) {
  activeTimeZone = isValidTimeZone(value) ? value : DEFAULT_TIME_ZONE;
}

function timeZoneLabel(value) {
  return String(value || DEFAULT_TIME_ZONE).replaceAll("_", " ");
}

function timeZoneOptions(current) {
  return Array.from(new Set([detectBrowserTimeZone(), current, ...COMMON_TIME_ZONES].filter(isValidTimeZone)));
}

function formatter(options) {
  return new Intl.DateTimeFormat("fr-FR", { ...options, timeZone: activeTimeZone });
}

function dateParts(value) {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  const parts = formatter({ year: "numeric", month: "2-digit", day: "2-digit" }).formatToParts(date);
  return parts.reduce((result, part) => {
    if (["year", "month", "day"].includes(part.type)) result[part.type] = part.value;
    return result;
  }, {});
}

const EMPTY = {
  updated_at: null,
  source: {},
  overview: {
    market_sentiment: "—",
    sentiment_score: 50,
    strongest_currency: "—",
    strongest_score: 50,
    weakest_currency: "—",
    weakest_score: 50,
    major_event: "Aucun événement majeur",
    major_event_time: "—",
    major_event_countdown: "Calendrier calme",
    trade_permission: "open",
    trade_permission_text: "Vérifier le calendrier avant toute entrée.",
  },
  quick_decision: {
    global_bias: "Analyse en attente de données",
    best_assets: [],
    avoid_assets: [],
    next_risk: "Aucun risque majeur détecté",
    next_risk_date: null,
    volatility_window: "Aucune fenêtre critique",
    volatility_window_start: null,
    volatility_window_end: null,
    trade_permission: "open",
    trade_permission_text: "Vérifier le calendrier avant toute entrée.",
    simple_rule: "Opposer une devise forte à une devise faible puis attendre la confirmation du prix.",
  },
  ai_insight: {
    title: "Analyse en attente",
    summary: "Le backend fondamental doit fournir les données du calendrier économique.",
    global_bias: "Neutre",
    confidence: 50,
    reasons: [],
  },
  economic_events: [],
  currency_strength: [],
  market_bias: [],
  opportunities: [],
  alerts: [],
  warnings: [],
  geopolitical: {
    status: "unavailable",
    risk_score: 0,
    risk_level: "Faible",
    headline_count: 0,
    summary: "Contexte géopolitique indisponible.",
  },
  methodology: { factors: [], formula: "", principle: "" },
  data_health: {
    archived_events: 0,
    confirmed_currencies: 0,
    provisional_currencies: 0,
    coverage_score: 0,
    message: "Moteur en attente.",
  },
};

const FUNDAMENTAL_CACHE_KEY = "fullmetrix_fundamental_dashboard_v4";
const FUNDAMENTAL_CACHE_MAX_AGE_MS = 24 * 60 * 60 * 1000;
const FUNDAMENTAL_REQUEST_TIMEOUT_MS = 12000;
const INITIAL_SKELETON_MAX_MS = 650;

function hasDashboardContent(value) {
  return Boolean(
    value?.updated_at ||
    value?.economic_events?.length ||
    value?.currency_strength?.length ||
    value?.market_bias?.length,
  );
}

function readCachedDashboard() {
  if (typeof window === "undefined") {
    return { data: EMPTY, hasData: false, cachedAt: null };
  }

  try {
    const raw = window.localStorage.getItem(FUNDAMENTAL_CACHE_KEY);
    if (!raw) return { data: EMPTY, hasData: false, cachedAt: null };

    const parsed = JSON.parse(raw);
    const savedAt = Number(parsed?.savedAt || 0);
    const freshEnough = savedAt > 0 && Date.now() - savedAt <= FUNDAMENTAL_CACHE_MAX_AGE_MS;
    if (!freshEnough || !parsed?.data) {
      window.localStorage.removeItem(FUNDAMENTAL_CACHE_KEY);
      return { data: EMPTY, hasData: false, cachedAt: null };
    }

    const normalized = normalizeDashboard(parsed.data);
    return {
      data: normalized,
      hasData: hasDashboardContent(normalized),
      cachedAt: savedAt,
    };
  } catch {
    return { data: EMPTY, hasData: false, cachedAt: null };
  }
}

function writeCachedDashboard(value) {
  if (typeof window === "undefined" || !hasDashboardContent(value)) return;
  try {
    window.localStorage.setItem(
      FUNDAMENTAL_CACHE_KEY,
      JSON.stringify({ savedAt: Date.now(), data: value }),
    );
  } catch {
    // Le cache est une optimisation : une erreur de stockage ne bloque jamais la page.
  }
}

function withRequestTimeout(promise, timeoutMs = FUNDAMENTAL_REQUEST_TIMEOUT_MS) {
  let timerId;
  const timeoutPromise = new Promise((_, reject) => {
    timerId = window.setTimeout(() => {
      reject(new Error("Le chargement prend trop de temps. Les dernières données disponibles restent affichées."));
    }, timeoutMs);
  });

  return Promise.race([promise, timeoutPromise]).finally(() => {
    if (timerId) window.clearTimeout(timerId);
  });
}

function clamp(value, min = 0, max = 100) {
  const number = Number(value);
  return Number.isFinite(number) ? Math.min(max, Math.max(min, number)) : min;
}

function normalizeDirection(value) {
  const text = String(value || "").toLowerCase();
  if (["up", "bullish", "positive", "long", "haussier", "hawkish"].includes(text)) return "up";
  if (["down", "bearish", "negative", "short", "baissier", "dovish"].includes(text)) return "down";
  return "neutral";
}

function directionLabel(value) {
  const direction = normalizeDirection(value);
  if (direction === "up") return "Haussier";
  if (direction === "down") return "Baissier";
  return "Neutre";
}

function impactLabel(value) {
  const text = String(value || "").toLowerCase();
  if (["3", "high", "fort", "critical", "danger"].includes(text)) return "Fort";
  if (["2", "medium", "moyen", "warning"].includes(text)) return "Moyen";
  return "Faible";
}

function formatTime(value) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return formatter({ hour: "2-digit", minute: "2-digit", hourCycle: "h23" }).format(date);
}

function formatEventDay(value) {
  if (!value) return "Aujourd’hui";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Aujourd’hui";

  const current = dateParts(new Date());
  const event = dateParts(date);
  const tomorrow = dateParts(new Date(Date.now() + 24 * 60 * 60 * 1000));
  const key = (parts) => parts ? `${parts.year}-${parts.month}-${parts.day}` : "";

  if (key(event) === key(current)) return "Aujourd’hui";
  if (key(event) === key(tomorrow)) return "Demain";

  return formatter({ weekday: "short", day: "2-digit", month: "short" }).format(date);
}

function formatDateTime(value) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return formatter({
    weekday: "short",
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  }).format(date);
}

function formatTimeRange(startValue, endValue, fallback = "Aucune fenêtre critique") {
  if (!startValue || !endValue) return fallback;
  const start = new Date(startValue);
  const end = new Date(endValue);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return fallback;
  return `${formatTime(start)} → ${formatTime(end)} (${timeZoneLabel(activeTimeZone)})`;
}

function formatVolatilityWindow(quick) {
  if (quick?.volatility_window_start && quick?.volatility_window_end) {
    return formatTimeRange(quick.volatility_window_start, quick.volatility_window_end, quick.volatility_window);
  }
  if (quick?.next_risk_date) {
    const eventDate = new Date(quick.next_risk_date);
    if (!Number.isNaN(eventDate.getTime())) {
      return formatTimeRange(
        new Date(eventDate.getTime() - 10 * 60 * 1000),
        new Date(eventDate.getTime() + 20 * 60 * 1000),
        quick.volatility_window,
      );
    }
  }
  return quick?.volatility_window || "Aucune fenêtre critique";
}

function hasPublishedValue(value) {
  return value !== null && value !== undefined && value !== "" && value !== "—";
}

function comparisonLevel(gap) {
  const absolute = Math.abs(Number(gap) || 0);
  if (absolute >= 25) return "Fort";
  if (absolute >= 12) return "Modéré";
  if (absolute >= 6) return "Léger";
  return "Équilibré";
}

function comparisonMessage(left, right, gap) {
  const absolute = Math.abs(Number(gap) || 0);
  if (absolute < 6) {
    return `Aucun avantage fondamental clair entre ${left} et ${right}.`;
  }

  const leader = gap > 0 ? left : right;
  const laggard = gap > 0 ? right : left;
  return `${leader} domine ${laggard} sur le score fondamental actuel.`;
}

function eventInterpretation(event) {
  if (event?.interpretation_text) return String(event.interpretation_text);
  if (typeof event?.interpretation === "string") return event.interpretation;
  return String(event?.interpretation?.text || event?.interpretation?.headline || "Analyse en attente.");
}

function derivedAssetImpacts(event) {
  if (Array.isArray(event.asset_impacts) && event.asset_impacts.length) return event.asset_impacts;
  const assets = event.affected_assets || event.interpretation?.assets || [];
  const eventDirection = normalizeDirection(event.interpretation?.sentiment);
  return assets.slice(0, 6).map((asset) => ({
    asset,
    direction: eventDirection,
    confidence: Number(event.confidence || event.interpretation?.confidence || 50),
    reason: eventInterpretation(event),
  }));
}

function marketSeries(market) {
  if (Array.isArray(market.series) && market.series.length > 1) return market.series.map(Number);
  const raw = Number(market.differential || 0);
  const sign = raw >= 0 ? 1 : -1;
  return [48, 50, 49, 53, 52, 56, 58, 62].map((value, index) => value + sign * Math.abs(raw) * index * 0.03);
}

function normalizeDashboard(payload) {
  const incoming = payload?.data && typeof payload.data === "object" ? payload.data : payload;
  if (!incoming || typeof incoming !== "object") return EMPTY;

  const rawCurrencies = Array.isArray(incoming.currency_strength) ? incoming.currency_strength : [];
  const currencies = rawCurrencies
    .map((item) => ({
      ...item,
      currency: item.currency || item.code || "—",
      flag: item.flag || FLAGS[item.currency] || "🌐",
      currencyName: item.currency_name || item.name || item.currency || item.code || "Devise",
      score: clamp(item.score ?? 50),
      scoreConfidence: clamp(item.score_confidence ?? item.confidence ?? 20),
      scoreStatus: item.score_status || "Données insuffisantes",
      scoreComponents: item.score_components || {},
      methodology: item.methodology || "",
      change_24h: Number(item.change_24h ?? item.change ?? 0),
      trend: item.trend || item.direction || "neutral",
      stance: item.stance || item.label || directionLabel(item.direction),
      summary: item.summary || "Score calculé à partir des facteurs macro récents.",
      history: Array.isArray(item.history) ? item.history : [],
      breakdown: item.breakdown || {},
      drivers: Array.isArray(item.drivers) ? item.drivers : [],
      driverDetails: Array.isArray(item.driver_details) ? item.driver_details : [],
      evidenceCount: Number(item.evidence_count || 0),
      signalCount: Number(item.signal_count ?? item.evidence_count ?? 0),
      pillarCoverage: Number(item.pillar_coverage || 0),
      dataQuality: item.data_quality || "insufficient",
      plainReading:
        item.plain_reading ||
        item.summary ||
        "Aucune lecture détaillée disponible.",
      explanation: item.explanation && typeof item.explanation === "object" ? item.explanation : {},
      watchList: Array.isArray(item.what_to_watch) ? item.what_to_watch : [],
    }))
    .sort((a, b) => b.score - a.score);

  const rawEvents = Array.isArray(incoming.economic_events) ? incoming.economic_events : [];
  const events = rawEvents.map((event, index) => ({
    ...event,
    id: event.id || `${event.currency || "event"}-${event.date || event.datetime || index}`,
    datetime: event.datetime || event.date || event.time,
    currency: event.currency || "",
    currencyName: event.currency_name || event.currency || "",
    flag: event.flag || FLAGS[event.currency] || "🌐",
    event: event.event || event.name || event.category || "Publication économique",
    eventOriginal: event.event_original || event.event || event.name || "",
    impact: event.impact || event.importance || "low",
    previous: event.previous ?? "—",
    forecast: event.forecast ?? "—",
    actual: event.actual === "—" ? null : event.actual,
    published: Boolean(event.published ?? (event.actual !== null && event.actual !== undefined && event.actual !== "—")),
    confidence: clamp(event.confidence ?? event.interpretation?.confidence ?? 50),
    interpretationText: eventInterpretation(event),
    traderExplanation:
      event.trader_explanation ||
      "Attends la publication puis vérifie si le prix confirme le scénario.",
    whatItMeans:
      event.what_it_means ||
      "Cette donnée peut modifier les anticipations du marché sur la devise.",
    higherScenario:
      event.higher_scenario ||
      "Un résultat supérieur aux attentes peut soutenir la devise.",
    lowerScenario:
      event.lower_scenario ||
      "Un résultat inférieur aux attentes peut affaiblir la devise.",
    tradeStatus: event.trade_status || "watch",
    tradeStatusText:
      event.trade_status_text ||
      "Surveiller l'événement sans anticiper le résultat.",
    traderChecklist: Array.isArray(event.trader_checklist)
      ? event.trader_checklist
      : [],
    surpriseLabel:
      event.surprise?.label ||
      "Comparer le résultat réel à la prévision.",
    dataState: event.data_state || event.data_explanation?.state || (event.published ? "published" : "pending"),
    dataExplanation: event.data_explanation && typeof event.data_explanation === "object"
      ? event.data_explanation
      : {},
    comparisonSummary: event.comparison_summary || event.data_explanation?.comparison?.summary || "",
    analysisSteps: Array.isArray(event.analysis_steps) ? event.analysis_steps : [],
    traderQuestions: Array.isArray(event.trader_questions) ? event.trader_questions : [],
    assetImpacts: derivedAssetImpacts(event),
  }));

  const rawMarkets = Array.isArray(incoming.market_bias) ? incoming.market_bias : [];
  const markets = rawMarkets.map((market) => ({
    ...market,
    symbol: market.symbol || "—",
    bias: market.bias || directionLabel(market.direction),
    direction: market.direction || normalizeDirection(market.bias),
    confidence: clamp(market.confidence ?? 50),
    horizon: market.horizon || "Intraday",
    thesis: market.thesis || market.reason || "Différentiel fondamental à confirmer par le prix.",
    invalidation: market.invalidation || "Une publication majeure ou un changement de régime peut invalider le scénario.",
    traderAction:
      market.trader_action ||
      "Attendre une confirmation technique hors fenêtre de news.",
    simpleRule:
      market.simple_rule ||
      "La paire monte lorsque la devise de base devient plus forte que la devise de cotation.",
    evidenceCount: Number(market.evidence_count || 0),
    baseScore: Number(market.base_score ?? 50),
    quoteScore: Number(market.quote_score ?? 50),
    drivers: Array.isArray(market.drivers) ? market.drivers : [],
    riskFactors: Array.isArray(market.risk_factors) ? market.risk_factors : [],
    explanation: market.explanation && typeof market.explanation === "object" ? market.explanation : {},
    baseSnapshot: market.base_snapshot || {},
    quoteSnapshot: market.quote_snapshot || {},
    dataConfidence: clamp(market.data_confidence ?? market.confidence ?? 50),
    series: marketSeries(market),
  }));

  const rawOverview = incoming.overview || {};
  const riskObject = typeof rawOverview.market_sentiment === "object" ? rawOverview.market_sentiment : {};
  const strongestObject = typeof rawOverview.strongest_currency === "object" ? rawOverview.strongest_currency : {};
  const weakestObject = typeof rawOverview.weakest_currency === "object" ? rawOverview.weakest_currency : {};
  const majorObject = typeof rawOverview.major_event === "object" ? rawOverview.major_event : {};
  const strongest = strongestObject.currency ? strongestObject : currencies[0] || {};
  const weakest = weakestObject.currency ? weakestObject : currencies[currencies.length - 1] || {};

  const overview = {
    market_sentiment:
      rawOverview.market_sentiment_value || riskObject.value ||
      (typeof rawOverview.market_sentiment === "string" ? rawOverview.market_sentiment : "Neutre"),
    sentiment_score: clamp(rawOverview.sentiment_score ?? riskObject.score ?? 50),
    strongest_currency: rawOverview.strongest_currency_code || strongest.currency || "—",
    strongest_score: clamp(rawOverview.strongest_score ?? strongest.score ?? 50),
    weakest_currency: rawOverview.weakest_currency_code || weakest.currency || "—",
    weakest_score: clamp(rawOverview.weakest_score ?? weakest.score ?? 50),
    major_event: rawOverview.major_event_name || majorObject.name ||
      (typeof rawOverview.major_event === "string" ? rawOverview.major_event : "Aucun événement majeur"),
    major_event_time: formatTime(rawOverview.major_event_date || majorObject.date),
    major_event_date: rawOverview.major_event_date || majorObject.date || null,
    major_event_countdown: rawOverview.major_event_countdown || "Calendrier calme",
    trade_permission: rawOverview.trade_permission || "open",
    trade_permission_text: rawOverview.trade_permission_text || "Vérifier le calendrier avant toute entrée.",
  };

  const insightRaw = incoming.ai_insight || {};
  const insight = {
    title: insightRaw.title || insightRaw.headline || "Lecture fondamentale FullMetrix",
    summary: insightRaw.summary || "Les données reçues ne permettent pas encore de produire une synthèse.",
    global_bias: insightRaw.global_bias || "Neutre",
    confidence: clamp(insightRaw.confidence ?? 50),
    reasons: Array.isArray(insightRaw.reasons)
      ? insightRaw.reasons
      : Array.isArray(insightRaw.bullets)
        ? insightRaw.bullets
        : [],
    disclaimer: insightRaw.disclaimer,
  };

  const opportunities = Array.isArray(incoming.opportunities) ? incoming.opportunities : [];
  const quickRaw = incoming.quick_decision || {};
  const quickDecision = {
    global_bias: quickRaw.global_bias || insight.global_bias,
    best_assets: Array.isArray(quickRaw.best_assets)
      ? quickRaw.best_assets
      : opportunities.slice(0, 3).map((item) => item.symbol).filter(Boolean),
    avoid_assets: Array.isArray(quickRaw.avoid_assets)
      ? quickRaw.avoid_assets
      : markets.filter((item) => item.direction === "neutral").slice(0, 2).map((item) => item.symbol),
    next_risk: quickRaw.next_risk || overview.major_event,
    next_risk_date: quickRaw.next_risk_date || overview.major_event_date,
    volatility_window: quickRaw.volatility_window || "Aucune fenêtre critique",
    volatility_window_start: quickRaw.volatility_window_start || null,
    volatility_window_end: quickRaw.volatility_window_end || null,
    trade_permission: quickRaw.trade_permission || overview.trade_permission || "open",
    trade_permission_text: quickRaw.trade_permission_text || overview.trade_permission_text,
    simple_rule: quickRaw.simple_rule || "Opposer une devise forte à une devise faible puis attendre la confirmation du prix.",
  };

  const alerts = (Array.isArray(incoming.alerts) ? incoming.alerts : []).map((alert, index) => ({
    ...alert,
    id: alert.id || `alert-${index}`,
    type:
      alert.type === "danger" ? "critical" :
      alert.type === "success" ? "opportunity" :
      alert.type || "warning",
    assets: Array.isArray(alert.assets) ? alert.assets : [],
    action: alert.action || "Confirmer le scénario avec le prix et votre gestion du risque.",
  }));

  return {
    ...EMPTY,
    ...incoming,
    updated_at: incoming.updated_at,
    overview,
    quick_decision: quickDecision,
    ai_insight: insight,
    economic_events: events,
    currency_strength: currencies,
    market_bias: markets,
    opportunities,
    alerts,
    education:
      incoming.education && typeof incoming.education === "object"
        ? incoming.education
        : {},
    warnings: Array.isArray(incoming.warnings) ? incoming.warnings : [],
  };
}

function IconBox({ icon: Icon, tone = "purple" }) {
  return <span className={`fmf-icon is-${tone}`}><Icon /></span>;
}

function Pill({ children, tone = "neutral" }) {
  return <span className={`fmf-pill is-${tone}`}>{children}</span>;
}

function CurrencyMark({ currency, className = "" }) {
  const code = CURRENCY_MARKS[currency] || String(currency || "FX").slice(0, 2).toUpperCase();
  return (
    <span className={`fmf-currency-mark ${className}`.trim()} aria-hidden="true">
      {code}
    </span>
  );
}


function scoreQualityLabel(currency) {
  if (!currency) return "Indisponible";
  return currency.scoreStatus || (
    currency.dataQuality === "confirmed"
      ? "Confirmé"
      : currency.dataQuality === "provisional"
        ? "Provisoire"
        : currency.dataQuality === "limited"
          ? "Partiel"
          : "Données insuffisantes"
  );
}

function ScoreQuality({ currency, compact = false }) {
  const quality = currency?.dataQuality || "insufficient";
  return (
    <span className={`fmf-score-quality is-${quality} ${compact ? "is-compact" : ""}`}>
      <i />
      {scoreQualityLabel(currency)}
    </span>
  );
}

function CurrencySelect({ label, value, currencies, onChange, disabledCurrency }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const rootRef = useRef(null);
  const selected = currencies.find((item) => item.currency === value) || currencies[0];

  useEffect(() => {
    const closeFromOutside = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target)) setOpen(false);
    };
    const closeFromKeyboard = (event) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", closeFromOutside);
    window.addEventListener("keydown", closeFromKeyboard);
    return () => {
      document.removeEventListener("mousedown", closeFromOutside);
      window.removeEventListener("keydown", closeFromKeyboard);
    };
  }, []);

  useEffect(() => {
    if (!open) setQuery("");
  }, [open]);

  const filtered = currencies.filter((item) => {
    const search = query.trim().toLowerCase();
    if (!search) return true;
    return `${item.currency} ${item.currencyName || ""} ${item.stance || ""}`.toLowerCase().includes(search);
  });

  return (
    <div className={`fmf-currency-select ${open ? "is-open" : ""}`} ref={rootRef}>
      <span className="fmf-currency-select-label">{label}</span>
      <button
        type="button"
        className="fmf-currency-select-trigger"
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((current) => !current)}
      >
        <CurrencyMark currency={selected?.currency} />
        <span className="fmf-currency-select-copy">
          <strong>{selected?.currency || "—"}</strong>
          <small>{selected?.currencyName || selected?.stance || "Devise"}</small>
        </span>
        <span className="fmf-currency-select-score">
          <b>{Math.round(selected?.score || 0)}</b>
          <small>/100</small>
        </span>
        <ChevronDown className="fmf-currency-select-chevron" />
      </button>

      {open ? (
        <div className="fmf-currency-select-menu" role="listbox" aria-label={label}>
          <div className="fmf-currency-select-search">
            <Search />
            <input
              autoFocus
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Rechercher une devise..."
            />
          </div>

          <div className="fmf-currency-select-options">
            {filtered.map((item) => {
              const disabled = item.currency === disabledCurrency;
              const active = item.currency === selected?.currency;
              return (
                <button
                  type="button"
                  role="option"
                  aria-selected={active}
                  disabled={disabled}
                  className={`${active ? "is-active" : ""} ${disabled ? "is-disabled" : ""}`}
                  key={item.currency}
                  onClick={() => {
                    if (disabled) return;
                    onChange(item.currency);
                    setOpen(false);
                  }}
                >
                  <CurrencyMark currency={item.currency} />
                  <span>
                    <strong>{item.currency}</strong>
                    <small>{item.currencyName || item.stance || "Devise"}</small>
                  </span>
                  <span className="fmf-currency-option-meta">
                    <b>{Math.round(item.score)}</b>
                    <ScoreQuality currency={item} compact />
                  </span>
                </button>
              );
            })}
            {!filtered.length ? <div className="fmf-currency-select-empty">Aucune devise trouvée.</div> : null}
          </div>
        </div>
      ) : null}
    </div>
  );
}


function signalTypeLabel(value) {
  const key = String(value || "").toLowerCase();
  if (key === "surprise") return "Réel vs prévision";
  if (key === "actual_vs_previous") return "Réel vs précédent";
  if (key === "expectation") return "Anticipation";
  if (key === "geopolitics") return "Géopolitique";
  return "Facteur macro";
}

function EventSelect({ value, events, onChange }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const rootRef = useRef(null);
  const selected = events.find((item) => item.id === value) || events[0];

  useEffect(() => {
    const closeFromOutside = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target)) setOpen(false);
    };
    const closeFromKeyboard = (event) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", closeFromOutside);
    window.addEventListener("keydown", closeFromKeyboard);
    return () => {
      document.removeEventListener("mousedown", closeFromOutside);
      window.removeEventListener("keydown", closeFromKeyboard);
    };
  }, []);

  useEffect(() => {
    if (!open) setQuery("");
  }, [open]);

  const filtered = events.filter((item) => {
    const search = query.trim().toLowerCase();
    if (!search) return true;
    return `${formatTime(item.datetime)} ${formatEventDay(item.datetime)} ${item.currency} ${item.event} ${item.category}`
      .toLowerCase()
      .includes(search);
  });

  if (!selected) return null;

  return (
    <div className={`fmf-event-select ${open ? "is-open" : ""}`} ref={rootRef}>
      <button
        type="button"
        className="fmf-event-select-trigger"
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((current) => !current)}
      >
        <CurrencyMark currency={selected.currency} className="fmf-event-select-mark" />
        <span className="fmf-event-select-current">
          <small>{formatTime(selected.datetime)} · {formatEventDay(selected.datetime)} · {selected.currency || "FX"}</small>
          <strong>{selected.event}</strong>
          <em>{selected.category || "Macroéconomie"} · {selected.published ? "résultat publié" : "publication à venir"}</em>
        </span>
        <Pill tone={impactLabel(selected.impact).toLowerCase()}>{impactLabel(selected.impact)}</Pill>
        <ChevronDown className="fmf-event-select-chevron" />
      </button>

      {open ? (
        <div className="fmf-event-select-menu" role="listbox" aria-label="Choisir un événement économique">
          <div className="fmf-event-select-search">
            <Search />
            <input
              autoFocus
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Rechercher par heure, devise ou indicateur..."
            />
            <span>{filtered.length}</span>
          </div>
          <div className="fmf-event-select-options">
            {filtered.map((item) => {
              const active = item.id === selected.id;
              const tone = impactLabel(item.impact).toLowerCase();
              return (
                <button
                  type="button"
                  role="option"
                  aria-selected={active}
                  className={active ? "is-active" : ""}
                  key={item.id}
                  onClick={() => {
                    onChange(item.id);
                    setOpen(false);
                  }}
                >
                  <span className="fmf-event-option-time">
                    <strong>{formatTime(item.datetime)}</strong>
                    <small>{formatEventDay(item.datetime)}</small>
                  </span>
                  <CurrencyMark currency={item.currency} className="fmf-event-option-mark" />
                  <span className="fmf-event-option-copy">
                    <strong>{item.event}</strong>
                    <small>{item.currency || "FX"} · {item.category || "Macroéconomie"}</small>
                    <em>{item.published ? item.interpretationText : item.whatItMeans}</em>
                  </span>
                  <span className="fmf-event-option-status">
                    <Pill tone={tone}>{impactLabel(item.impact)}</Pill>
                    <small>{item.published ? "Publié" : "À venir"}</small>
                  </span>
                </button>
              );
            })}
            {!filtered.length ? (
              <div className="fmf-event-select-empty">Aucun événement ne correspond à cette recherche.</div>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  );
}

function Direction({ value, compact = false }) {
  const direction = normalizeDirection(value);
  const Icon = direction === "up" ? ArrowUp : direction === "down" ? ArrowDown : Activity;
  return (
    <span className={`fmf-direction is-${direction} ${compact ? "is-compact" : ""}`}>
      <Icon />
      {directionLabel(direction)}
    </span>
  );
}

function SectionHeader({ icon, title, subtitle, action, tone = "purple" }) {
  return (
    <div className="fmf-section-head">
      <div className="fmf-section-title">
        <IconBox icon={icon} tone={tone} />
        <div>
          <h2>{title}</h2>
          {subtitle ? <p>{subtitle}</p> : null}
        </div>
      </div>
      {action}
    </div>
  );
}

function EmptyState({ title = "Aucune donnée disponible", text = "Le backend n'a retourné aucun élément pour cette section." }) {
  return (
    <div className="fmf-empty">
      <CloudOff />
      <strong>{title}</strong>
      <span>{text}</span>
    </div>
  );
}

function Ring({ value = 0, label = "Confiance IA" }) {
  const safe = clamp(value);
  const radius = 40;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (safe / 100) * circumference;
  return (
    <div className="fmf-ring-wrap">
      <div className="fmf-ring">
        <svg className="fmf-ring-svg" viewBox="0 0 110 110" aria-hidden="true">
          <defs>
            <linearGradient id="fmf-ring-gradient" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stopColor="#7637ee" />
              <stop offset="100%" stopColor="#d94fe9" />
            </linearGradient>
          </defs>
          <circle className="fmf-ring-bg" cx="55" cy="55" r={radius} fill="none" strokeWidth="8" />
          <circle
            cx="55"
            cy="55"
            r={radius}
            fill="none"
            stroke="url(#fmf-ring-gradient)"
            strokeWidth="8"
            strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={offset}
            transform="rotate(-90 55 55)"
          />
        </svg>
        <strong>{Math.round(safe)}%</strong>
      </div>
      <span>{label}</span>
    </div>
  );
}

function Sparkline({ values = [], direction = "neutral" }) {
  const source = values.length > 1 ? values.map(Number) : [48, 50, 49, 53, 52, 56, 58, 62];
  const minimum = Math.min(...source);
  const maximum = Math.max(...source);
  const range = maximum - minimum || 1;
  const points = source
    .map((value, index) => {
      const x = 3 + (index / (source.length - 1)) * 94;
      const y = 35 - ((value - minimum) / range) * 29;
      return `${x},${y}`;
    })
    .join(" ");
  return (
    <svg className={`fmf-spark is-${direction}`} viewBox="0 0 100 40" preserveAspectRatio="none" aria-hidden="true">
      <polyline points={points} fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function Modal({ open, onClose, title, subtitle, size = "medium", children }) {
  useEffect(() => {
    if (!open) return undefined;

    const escape = (event) => {
      if (event.key === "Escape") onClose();
    };

    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", escape);

    return () => {
      document.body.style.overflow = previous;
      window.removeEventListener("keydown", escape);
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="fmf-backdrop" onMouseDown={onClose}>
      <section
        className={`fmf-modal is-${size}`}
        onMouseDown={(event) => event.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={title}
      >
        <header className="fmf-modal-header">
          <div className="fmf-modal-heading">
            <span className="fmf-modal-eyebrow">FULLMETRIX · ANALYSE FOREX</span>
            <h2>{title}</h2>
            {subtitle ? <p>{subtitle}</p> : null}
          </div>

          <button type="button" className="fmf-modal-close" onClick={onClose} aria-label="Fermer">
            <X />
          </button>
        </header>

        <div className="fmf-modal-body">{children}</div>
      </section>
    </div>
  );
}

function EventRows({ events, onSelect, limit }) {
  const rows = typeof limit === "number" ? events.slice(0, limit) : events;
  if (!rows.length) return <EmptyState title="Aucun événement économique" />;

  const impactCounts = rows.reduce(
    (accumulator, event) => {
      const level = impactLabel(event.impact).toLowerCase();
      accumulator[level] = (accumulator[level] || 0) + 1;
      return accumulator;
    },
    { fort: 0, moyen: 0, faible: 0 },
  );

  return (
    <div className="fmf-calendar-shell">
      <div className="fmf-calendar-meta">
        <span className="fmf-calendar-total">
          <CalendarDays />
          {rows.length} événement{rows.length > 1 ? "s" : ""}
        </span>

        <div className="fmf-calendar-legend" aria-label="Niveaux d'impact">
          <span className="is-high"><i />{impactCounts.fort} fort</span>
          <span className="is-medium"><i />{impactCounts.moyen} moyen</span>
          <span className="is-low"><i />{impactCounts.faible} faible</span>
        </div>

        <span className="fmf-calendar-zone">
          <Clock3 /> {timeZoneLabel(activeTimeZone)}
        </span>
      </div>

      <div className="fmf-event-list">
        <div className="fmf-event-grid fmf-event-head">
          <span>Heure</span>
          <span>Événement</span>
          <span>Impact</span>
          <span>Prévision</span>
          <span>Résultat</span>
          <span>Lecture trader</span>
        </div>

        {rows.map((event) => {
          const surprise = Number(event.surprise?.raw ?? event.surprise);
          const published = hasPublishedValue(event.actual);
          const resultTone = Number.isFinite(surprise)
            ? surprise > 0
              ? "positive"
              : surprise < 0
                ? "negative"
                : "neutral"
            : "neutral";
          const impactTone = impactLabel(event.impact).toLowerCase();

          return (
            <button
              key={event.id}
              type="button"
              className={`fmf-event-grid fmf-event-row is-${impactTone}`}
              onClick={() => onSelect(event)}
            >
              <span className="fmf-event-when">
                <strong>{formatTime(event.datetime)}</strong>
                <small>{formatEventDay(event.datetime)}</small>
              </span>

              <span className="fmf-event-name">
                <CurrencyMark currency={event.currency} className="fmf-event-flag" />
                <span>
                  <strong>{event.event}</strong>
                  <small>
                    <em>{event.currency || "FX"}</em>
                    {event.category || "Économie"}
                  </small>
                </span>
              </span>

              <span className="fmf-event-impact">
                <Pill tone={impactTone}>{impactLabel(event.impact)}</Pill>
              </span>

              <span className="fmf-event-value">
                <small>Prévision</small>
                <strong>{event.forecast ?? "—"}</strong>
              </span>

              <span className={`fmf-event-value fmf-event-actual is-${published ? resultTone : "pending"}`}>
                <small>{published ? "Publié" : "Statut"}</small>
                <strong>{published ? event.actual : "À venir"}</strong>
              </span>

              <span className="fmf-event-reading">
                <span>{event.interpretationText}</span>
                <i><Eye /></i>
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function EventDetail({ event }) {
  if (!event) return null;

  const published = Boolean(event.published || hasPublishedValue(event.actual));
  const impactTone = impactLabel(event.impact).toLowerCase();
  const surpriseValue = typeof event.surprise === "object"
    ? event.surprise?.raw
    : event.surprise;
  const surprise = Number(surpriseValue);
  const surpriseTone = Number.isFinite(surprise)
    ? surprise > 0
      ? "positive"
      : surprise < 0
        ? "negative"
        : "neutral"
    : "neutral";
  const assets = Array.isArray(event.assetImpacts) ? event.assetImpacts : [];
  const checklist = event.traderChecklist?.length
    ? event.traderChecklist
    : [
        "Ne pas anticiper le résultat avant la publication.",
        "Attendre la stabilisation du spread.",
        "Confirmer la direction avec le prix.",
      ];

  return (
    <div className="fmf-event-detail">
      <section className={`fmf-event-hero is-${impactTone}`}>
        <div className="fmf-event-hero-main">
          <CurrencyMark currency={event.currency} className="fmf-event-hero-flag" />
          <div>
            <span className="fmf-event-hero-code">{event.currency || "FX"}</span>
            <strong>{event.category || "Événement économique"}</strong>
            <small><CalendarDays /> {formatDateTime(event.datetime)} · {timeZoneLabel(activeTimeZone)}</small>
          </div>
        </div>

        <div className="fmf-event-hero-status">
          <Pill tone={impactTone}>Impact {impactLabel(event.impact).toLowerCase()}</Pill>
          <span className={`fmf-publication-status is-${published ? "published" : "pending"}`}>
            <i /> {published ? "Résultat publié" : "Publication à venir"}
          </span>
        </div>
      </section>

      <section className={`fmf-trade-status is-${event.tradeStatus || "watch"}`}>
        <span><ShieldAlert /></span>
        <div>
          <small>Ce que le trader doit faire maintenant</small>
          <strong>{event.tradeStatusText}</strong>
        </div>
      </section>

      <section className="fmf-event-kpis">
        <div>
          <span>Prévision</span>
          <strong>{event.forecast ?? "—"}</strong>
          <small>Ce que le marché attend</small>
        </div>
        <div className={published ? `is-${surpriseTone}` : ""}>
          <span>Résultat réel</span>
          <strong>{published ? event.actual : "À venir"}</strong>
          <small>{published ? "Chiffre réellement publié" : "Pas encore disponible"}</small>
        </div>
        <div>
          <span>Précédent</span>
          <strong>{event.previous ?? "—"}</strong>
          <small>Résultat de la période précédente</small>
        </div>
        <div>
          <span>Lecture de la surprise</span>
          <strong className={`fmf-text-${surpriseTone}`}>
            {event.surpriseLabel || (surpriseValue ?? "—")}
          </strong>
          <small>Réel comparé à la prévision</small>
        </div>
        <div>
          <span>Confiance</span>
          <strong>{Math.round(event.confidence || 0)}%</strong>
          <small>Fiabilité de l'interprétation automatique</small>
        </div>
      </section>

      <section className="fmf-data-story">
        <header>
          <span><Database /></span>
          <div>
            <small>Explication complète des chiffres</small>
            <strong>{event.dataExplanation?.headline || "Comment lire cette publication"}</strong>
          </div>
        </header>
        <div className="fmf-data-story-grid">
          {[event.dataExplanation?.previous, event.dataExplanation?.forecast, event.dataExplanation?.actual]
            .filter(Boolean)
            .map((item) => (
              <article key={item.label}>
                <small>{item.label}</small>
                <strong>{item.value ?? "—"}</strong>
                <p>{item.explanation}</p>
              </article>
            ))}
        </div>
        <div className="fmf-data-story-reading">
          <BarChart3 />
          <span>
            <small>Comparaison et mécanisme de marché</small>
            <strong>{event.dataExplanation?.comparison?.summary || event.comparisonSummary || event.surpriseLabel}</strong>
            <p>{event.dataExplanation?.market_mechanism || event.traderExplanation}</p>
          </span>
        </div>
        {event.analysisSteps?.length ? (
          <div className="fmf-analysis-steps">
            {event.analysisSteps.map((step, index) => (
              <span key={`${step}-${index}`}><b>{index + 1}</b>{step}</span>
            ))}
          </div>
        ) : null}
      </section>

      <section className="fmf-understand-grid">
        <article>
          <span><CircleDollarSign /></span>
          <div>
            <small>Ce que cette donnée mesure</small>
            <strong>{event.whatItMeans}</strong>
          </div>
        </article>
        <article className="is-positive">
          <span><ArrowUp /></span>
          <div>
            <small>Si le résultat est supérieur aux attentes</small>
            <strong>{event.higherScenario}</strong>
          </div>
        </article>
        <article className="is-negative">
          <span><ArrowDown /></span>
          <div>
            <small>Si le résultat est inférieur aux attentes</small>
            <strong>{event.lowerScenario}</strong>
          </div>
        </article>
      </section>

      <section className="fmf-trader-reading">
        <span className="fmf-trader-reading-icon"><Sparkles /></span>
        <div>
          <span>Traduction simple FullMetrix</span>
          <strong>{event.interpretationText}</strong>
          <small>{event.traderExplanation}</small>
        </div>
      </section>

      <div className="fmf-detail-section-title">
        <div>
          <h3>Conséquence possible sur les paires</h3>
          <p>La direction reste indicative jusqu'à la confirmation du prix.</p>
        </div>
        <span>{assets.length} paire{assets.length > 1 ? "s" : ""}</span>
      </div>

      {assets.length ? (
        <section className="fmf-event-assets">
          {assets.map((item) => {
            const confidence = clamp(item.confidence ?? event.confidence ?? 50);
            const direction = normalizeDirection(item.direction);
            return (
              <article key={item.asset} className={`fmf-event-asset is-${direction}`}>
                <header>
                  <div>
                    <span>PAIRE FOREX</span>
                    <strong>{item.asset}</strong>
                  </div>
                  <Direction value={direction} compact />
                </header>
                <p>{item.reason || event.interpretationText}</p>
                {item.action ? <small className="fmf-asset-action">{item.action}</small> : null}
                <footer>
                  <span>Confiance</span>
                  <div className="fmf-event-confidence"><i style={{ width: `${confidence}%` }} /></div>
                  <strong>{Math.round(confidence)}%</strong>
                </footer>
              </article>
            );
          })}
        </section>
      ) : (
        <EmptyState title="Impact par paire indisponible" text="Le moteur n'a pas encore calculé les paires concernées." />
      )}

      <section className="fmf-checklist">
        <header>
          <CheckCircle2 />
          <strong>Plan simple avant de trader</strong>
        </header>
        <div>
          {checklist.map((item) => (
            <span key={item}><CheckCircle2 /> {item}</span>
          ))}
        </div>
      </section>
    </div>
  );
}

function Comparator({ currencies, onOpen }) {
  const [left, setLeft] = useState("");
  const [right, setRight] = useState("");

  useEffect(() => {
    if (!currencies.length) return;
    setLeft((value) => value || currencies[0]?.currency || "");
    setRight((value) => value || currencies[Math.min(3, currencies.length - 1)]?.currency || "");
  }, [currencies]);

  const leftCurrency = currencies.find((item) => item.currency === left) || currencies[0];
  const rightCurrency = currencies.find((item) => item.currency === right) || currencies[1] || currencies[0];
  const leftScore = Number(leftCurrency?.score || 0);
  const rightScore = Number(rightCurrency?.score || 0);
  const confirmedEvidence = Number(leftCurrency?.evidenceCount || 0) + Number(rightCurrency?.evidenceCount || 0);
  const evidence = Number(leftCurrency?.signalCount || 0) + Number(rightCurrency?.signalCount || 0);
  const pairConfidence = Math.round((Number(leftCurrency?.scoreConfidence || 20) + Number(rightCurrency?.scoreConfidence || 20)) / 2);
  const gap = leftScore - rightScore;
  const direction = evidence === 0
    ? "neutral"
    : gap > 5
      ? "up"
      : gap < -5
        ? "down"
        : "neutral";
  const pair = `${left || "—"}/${right || "—"}`;
  const advantage = evidence === 0
    ? "Données insuffisantes"
    : Math.abs(gap) < 6
      ? "Aucun avantage clair"
      : gap > 0
        ? `${left} est plus fort`
        : `${right} est plus fort`;
  const leftShare = evidence === 0 ? 50 : clamp(50 + gap / 2, 10, 90);
  const confidence = evidence === 0
    ? Math.min(35, pairConfidence)
    : Math.min(94, Math.round(pairConfidence * 0.58 + Math.abs(gap) * 2 + Math.min(evidence, 6) * 2));

  const updateLeft = (value) => {
    if (value === right) setRight(left);
    setLeft(value);
  };

  const updateRight = (value) => {
    if (value === left) setLeft(right);
    setRight(value);
  };

  const swapCurrencies = () => {
    setLeft(right);
    setRight(left);
  };

  const plainReading = evidence === 0
    ? `Les deux scores sont à 50/100 faute de résultats publiés suffisants. Cela ne signifie pas que ${left} et ${right} ont exactement la même force.`
    : direction === "up"
      ? `${left} est plus fort que ${right}. Comme ${left} est la devise de base, ${pair} peut monter après confirmation.`
      : direction === "down"
        ? `${right} est plus fort que ${left}. Comme ${right} est la devise de cotation, ${pair} peut baisser après confirmation.`
        : `Les scores de ${left} et ${right} sont trop proches. Aucun trade fondamental propre n'est confirmé.`;

  return (
    <article className="fmf-card fmf-tool-card fmf-comparator-card">
      <SectionHeader
        icon={CircleDollarSign}
        title="Comparateur de devises"
        subtitle="Comprends simplement pourquoi une paire peut monter ou baisser."
        action={<span className="fmf-premium-label"><Sparkles /> Lecture guidée</span>}
      />

      {!leftCurrency || !rightCurrency ? (
        <EmptyState title="Devises indisponibles" />
      ) : (
        <>
          <div className="fmf-compare-toolbar">
            <CurrencySelect
              label="Devise de base — première devise"
              value={left}
              currencies={currencies}
              onChange={updateLeft}
              disabledCurrency={right}
            />

            <button type="button" className="fmf-swap-button" onClick={swapCurrencies} aria-label="Inverser les devises">
              <RefreshCw />
            </button>

            <CurrencySelect
              label="Devise de cotation — deuxième devise"
              value={right}
              currencies={currencies}
              onChange={updateRight}
              disabledCurrency={left}
            />
          </div>

          <div className="fmf-pair-rule">
            <BarChart3 />
            <span>
              <strong>Règle simple :</strong> {pair} monte lorsque {left} devient plus fort que {right}. Il baisse lorsque {right} devient plus fort que {left}.
            </span>
          </div>

          <div className="fmf-compare-stage">
            <button type="button" className="fmf-compare-currency" onClick={() => onOpen(leftCurrency)}>
              <CurrencyMark currency={leftCurrency.currency} className="fmf-compare-flag" />
              <div>
                <span>Devise de base</span>
                <strong>{leftCurrency.currency}</strong>
                <small>{leftCurrency.stance || directionLabel(leftCurrency.trend)}</small>
                <ScoreQuality currency={leftCurrency} compact />
              </div>
              <b>{Math.round(leftScore)}</b>
              <div className="fmf-score-track"><i style={{ width: `${clamp(leftScore)}%` }} /></div>
              <em>{leftCurrency.evidenceCount || 0} publication(s) · {leftCurrency.signalCount || 0} facteur(s)</em>
            </button>

            <div className="fmf-compare-center">
              <span className={`fmf-edge-badge is-${direction}`}>{evidence === 0 ? "En attente" : comparisonLevel(gap)}</span>
              <strong>{pair}</strong>
              <Direction value={direction} />
              <div className="fmf-balance-meter" aria-label="Répartition des forces">
                <i className="is-left" style={{ width: `${leftShare}%` }} />
                <i className="is-right" style={{ width: `${100 - leftShare}%` }} />
              </div>
              <span className="fmf-gap-value">{gap > 0 ? "+" : ""}{gap.toFixed(1)} pts</span>
              <small>{advantage}</small>
            </div>

            <button type="button" className="fmf-compare-currency" onClick={() => onOpen(rightCurrency)}>
              <CurrencyMark currency={rightCurrency.currency} className="fmf-compare-flag" />
              <div>
                <span>Devise de cotation</span>
                <strong>{rightCurrency.currency}</strong>
                <small>{rightCurrency.stance || directionLabel(rightCurrency.trend)}</small>
                <ScoreQuality currency={rightCurrency} compact />
              </div>
              <b>{Math.round(rightScore)}</b>
              <div className="fmf-score-track"><i style={{ width: `${clamp(rightScore)}%` }} /></div>
              <em>{rightCurrency.evidenceCount || 0} publication(s) · {rightCurrency.signalCount || 0} facteur(s)</em>
            </button>
          </div>

          <div className={`fmf-forex-readout is-${direction}`}>
            <span><Target /></span>
            <div>
              <small>Traduction pour le trader</small>
              <strong>{plainReading}</strong>
              <p>
                {evidence === 0
                  ? "Attends de nouvelles publications avant d'utiliser ce comparateur pour décider."
                  : direction === "neutral"
                    ? "Aucun avantage clair : mieux vaut patienter."
                    : "Le fondamental donne le contexte. Le prix doit ensuite confirmer l'entrée."}
              </p>
            </div>
            <b>{confidence}%</b>
          </div>

          <div className="fmf-score-help fmf-score-help-v2">
            <ShieldAlert />
            <span>
              <strong>Pourquoi un score peut rester proche de 50 ?</strong>
              Le moteur additionne les surprises publiées, les anticipations, l'historique et la géopolitique.
              Un score proche de 50 signifie que ces facteurs se compensent ou que la confiance reste faible.
            </span>
            <span className="fmf-score-help-meta">
              <b>{confirmedEvidence}</b> publication(s) confirmée(s)
              <b>{evidence}</b> facteur(s) analysé(s)
              <b>{pairConfidence}%</b> confiance données
            </span>
          </div>
        </>
      )}
    </article>
  );
}

function ImpactTool({ events, onOpen }) {
  const [selectedId, setSelectedId] = useState("");

  useEffect(() => {
    if (!events.length) {
      setSelectedId("");
      return;
    }
    setSelectedId((current) =>
      events.some((item) => item.id === current) ? current : events[0].id,
    );
  }, [events]);

  const selected = events.find((item) => item.id === selectedId) || events[0];
  const impacts = Array.isArray(selected?.assetImpacts) ? selected.assetImpacts.slice(0, 4) : [];
  const explanation = selected?.dataExplanation || {};
  const comparison = explanation.comparison || {};

  return (
    <article className="fmf-card fmf-tool-card fmf-impact-card">
      <SectionHeader
        icon={Zap}
        title="Impact par actif"
        subtitle="Choisis une publication, comprends les chiffres puis visualise les paires exposées."
        tone="amber"
        action={selected ? <Pill tone={impactLabel(selected.impact).toLowerCase()}>Impact {impactLabel(selected.impact)}</Pill> : null}
      />

      {!selected ? (
        <EmptyState title="Aucun événement à analyser" />
      ) : (
        <>
          <EventSelect value={selected.id} events={events} onChange={setSelectedId} />

          <section className="fmf-impact-explainer">
            <header>
              <span><Sparkles /></span>
              <div>
                <small>Comprendre cette donnée</small>
                <strong>{explanation.headline || selected.interpretationText}</strong>
              </div>
            </header>
            <p>{explanation.why_it_matters || selected.whatItMeans}</p>
            <div className="fmf-impact-data-grid">
              <div>
                <small>Précédent</small>
                <strong>{selected.previous ?? "—"}</strong>
                <span>{explanation.previous?.explanation || "Dernière valeur connue, utilisée pour mesurer l'évolution."}</span>
              </div>
              <div>
                <small>Prévision</small>
                <strong>{selected.forecast ?? "—"}</strong>
                <span>{explanation.forecast?.explanation || "Consensus attendu par le marché avant la publication."}</span>
              </div>
              <div className={selected.published ? `is-${normalizeDirection(comparison.direction)}` : "is-pending"}>
                <small>Résultat réel</small>
                <strong>{selected.published ? selected.actual : "À venir"}</strong>
                <span>{explanation.actual?.explanation || "Le chiffre publié déclenche la comparaison avec les attentes."}</span>
              </div>
            </div>
            <div className="fmf-impact-comparison">
              <BarChart3 />
              <span>
                <small>Lecture du marché</small>
                <strong>{comparison.summary || selected.comparisonSummary || selected.traderExplanation}</strong>
              </span>
            </div>
          </section>

          {impacts.length ? (
            <div className="fmf-impact-list">
              {impacts.map((item) => {
                const confidence = clamp(item.confidence ?? selected.confidence ?? 50);
                const direction = normalizeDirection(item.direction);
                return (
                  <div key={item.asset} className={`is-${direction}`}>
                    <span className="fmf-impact-symbol">
                      <strong>{item.asset}</strong>
                      <small>{item.reason || selected.interpretationText}</small>
                    </span>
                    <span className="fmf-impact-signal">
                      <Direction value={direction} compact />
                      <span className="fmf-impact-confidence"><i style={{ width: `${confidence}%` }} /></span>
                      <small>{Math.round(confidence)}%</small>
                    </span>
                  </div>
                );
              })}
            </div>
          ) : (
            <EmptyState title="Aucune paire calculée" text="Le moteur n'a pas encore associé cet événement à des actifs précis." />
          )}

          <button type="button" className="fmf-tool-cta" onClick={() => onOpen(selected)}>
            <span>
              <small>Ouvrir l'explication complète</small>
              <strong>Définition, comparaison, scénarios, paires et plan trader</strong>
            </span>
            <ArrowRight />
          </button>
        </>
      )}
    </article>
  );
}

function RiskTool({ events, onOpen }) {
  const eventTimestamp = (event) => {
    if (!event?.datetime) return Number.MAX_SAFE_INTEGER;
    const timestamp = new Date(event.datetime).getTime();
    return Number.isFinite(timestamp) ? timestamp : Number.MAX_SAFE_INTEGER;
  };

  const upcoming = [...events]
    .filter((event) => eventTimestamp(event) >= Date.now() - 30 * 60 * 1000)
    .sort((a, b) => eventTimestamp(a) - eventTimestamp(b))
    .slice(0, 4);

  const highRiskCount = upcoming.filter((event) => impactLabel(event.impact) === "Fort").length;

  return (
    <article className="fmf-card fmf-tool-card fmf-risk-card">
      <SectionHeader
        icon={Clock3}
        title="Risque horaire"
        subtitle="Repère les fenêtres où les spreads et la volatilité peuvent augmenter."
        tone="red"
        action={<span className={`fmf-risk-count ${highRiskCount ? "is-danger" : ""}`}>{upcoming.length} fenêtre{upcoming.length > 1 ? "s" : ""}</span>}
      />

      {!upcoming.length ? (
        <EmptyState title="Aucun risque horaire proche" />
      ) : (
        <div className="fmf-risk-list">
          {upcoming.map((event, index) => {
            const impact = impactLabel(event.impact);
            const impactTone = impact.toLowerCase();

            return (
              <button type="button" key={event.id} onClick={() => onOpen(event)}>
                <span className={`fmf-risk-index is-${impactTone}`}>{String(index + 1).padStart(2, "0")}</span>
                <span className="fmf-risk-time">
                  <time>{formatTime(event.datetime)}</time>
                  <small>{formatEventDay(event.datetime)}</small>
                </span>
                <span className="fmf-risk-copy">
                  <strong>{event.event}</strong>
                  <small>{event.currency || "FX"} · publication économique</small>
                </span>
                <Pill tone={impactTone}>{impact}</Pill>
                <ArrowRight />
              </button>
            );
          })}
        </div>
      )}

      {upcoming.length ? (
        <div className="fmf-risk-guidance">
          <ShieldAlert />
          <span>
            <strong>Règle de prudence</strong>
            Évite une nouvelle entrée juste avant une publication forte.
          </span>
        </div>
      ) : null}
    </article>
  );
}


function TimeZoneSettings({ value, onChange }) {
  const detected = detectBrowserTimeZone();
  const options = timeZoneOptions(value);

  return (
    <div className="fmf-timezone-settings">
      <div className="fmf-timezone-preview">
        <span><Globe2 /></span>
        <div>
          <small>Heure actuellement affichée</small>
          <strong>{formatDateTime(new Date().toISOString())}</strong>
          <p>Les dates du backend restent en UTC. FullMetrix les convertit uniquement pour l’affichage.</p>
        </div>
      </div>

      <label className="fmf-timezone-field">
        <span>Fuseau horaire du trader</span>
        <div>
          <Globe2 />
          <select value={value} onChange={(event) => onChange(event.target.value)}>
            {options.map((zone) => (
              <option value={zone} key={zone}>{timeZoneLabel(zone)}</option>
            ))}
          </select>
          <ChevronDown />
        </div>
      </label>

      <button type="button" className="fmf-timezone-detect" onClick={() => onChange(detected)}>
        <Target /> Utiliser le fuseau détecté : {timeZoneLabel(detected)}
      </button>

      <div className="fmf-timezone-help">
        <CheckCircle2 />
        <span>Ce réglage est enregistré sur cet appareil et s’applique au calendrier, aux risques horaires et aux pop-ups.</span>
      </div>
    </div>
  );
}

function TraderReadingGuide({ overview, quick, strongest, weakest }) {
  const permission = quick.trade_permission || overview.trade_permission || "open";
  const permissionTone = permission === "blocked" ? "blocked" : permission === "caution" ? "caution" : "open";
  const permissionLabel = permission === "blocked" ? "Ne pas entrer" : permission === "caution" ? "Prudence" : "Marché tradable";

  return (
    <section className="fmf-card fmf-reading-guide">
      <div className={`fmf-reading-status is-${permissionTone}`}>
        <span><ShieldAlert /></span>
        <div>
          <small>1 · Puis-je trader maintenant ?</small>
          <strong>{permissionLabel}</strong>
          <p>{quick.trade_permission_text || overview.trade_permission_text}</p>
        </div>
      </div>
      <div className="fmf-reading-step">
        <span><CircleDollarSign /></span>
        <div>
          <small>2 · Quelles devises opposer ?</small>
          <strong>{strongest?.currency || "—"} forte contre {weakest?.currency || "—"} faible</strong>
          <p>Privilégie les paires où l’écart de score est clair et les données suffisamment fiables.</p>
        </div>
      </div>
      <div className="fmf-reading-step">
        <span><Target /></span>
        <div>
          <small>3 · Quand entrer ?</small>
          <strong>Seulement après confirmation du prix</strong>
          <p>{quick.simple_rule}</p>
        </div>
      </div>
    </section>
  );
}

function EngineStatus({ data }) {
  const health = data?.data_health || {};
  const geo = data?.geopolitical || {};
  const factors = Array.isArray(data?.methodology?.factors) ? data.methodology.factors : [];
  const coverage = clamp(health.coverage_score || 0);

  return (
    <section className="fmf-card fmf-engine-strip">
      <div className="fmf-engine-intro">
        <span className="fmf-engine-icon"><Layers3 /></span>
        <div>
          <small>Moteur fondamental FullMetrix 2.0</small>
          <strong>Une lecture macro multi-facteurs, pas un simple calendrier</strong>
          <p>{health.message || "Le moteur combine les données disponibles sans inventer de signal."}</p>
        </div>
      </div>

      <div className="fmf-engine-metrics">
        <div>
          <Database />
          <span><small>Historique conservé</small><strong>{health.archived_events || 0} événements</strong></span>
        </div>
        <div>
          <Gauge />
          <span><small>Couverture moteur</small><strong>{Math.round(coverage)}%</strong></span>
        </div>
        <div>
          <Globe2 />
          <span><small>Risque géopolitique</small><strong>{geo.risk_level || "Faible"} · {Math.round(Number(geo.risk_score || 0))}/100</strong></span>
        </div>
      </div>

      <div className="fmf-engine-factors">
        {(factors.length ? factors : ["Surprises macro", "Anticipations", "Historique", "Géopolitique"]).slice(0, 6).map((factor) => (
          <span key={factor}><CheckCircle2 /> {factor}</span>
        ))}
      </div>
    </section>
  );
}



function SkeletonBlock({ className = "" }) {
  return <span className={`fmf-skeleton-block ${className}`.trim()} aria-hidden="true" />;
}

function FullMetrixLoader() {
  return (
    <motion.section
      className="fmf-dashboard-skeleton"
      role="status"
      aria-live="polite"
      aria-label="Préparation de l'analyse fondamentale"
      initial={{ opacity: 0, y: 7 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.2, ease: "easeOut" }}
    >
      <div className="fmf-skeleton-heading">
        <span className="fmf-skeleton-heading-icon" aria-hidden="true"><BarChart3 /></span>
        <div>
          <strong>Préparation de l’analyse fondamentale</strong>
          <span>Les données économiques et les scénarios se mettent en place.</span>
        </div>
      </div>

      <div className="fmf-skeleton-summary" aria-hidden="true">
        {Array.from({ length: 4 }).map((_, index) => (
          <article className="fmf-skeleton-card fmf-skeleton-summary-card" key={`summary-${index}`}>
            <div className="fmf-skeleton-card-top">
              <SkeletonBlock className="is-icon" />
              <SkeletonBlock className="is-pill" />
            </div>
            <SkeletonBlock className="is-label" />
            <SkeletonBlock className="is-value" />
            <div className="fmf-skeleton-card-foot">
              <SkeletonBlock className="is-small" />
              <SkeletonBlock className="is-number" />
            </div>
          </article>
        ))}
      </div>

      <article className="fmf-skeleton-card fmf-skeleton-decision" aria-hidden="true">
        <div className="fmf-skeleton-decision-main">
          <SkeletonBlock className="is-icon-large" />
          <div>
            <SkeletonBlock className="is-label-wide" />
            <SkeletonBlock className="is-text-wide" />
          </div>
        </div>
        {Array.from({ length: 4 }).map((_, index) => (
          <div className="fmf-skeleton-decision-item" key={`decision-${index}`}>
            <SkeletonBlock className="is-small" />
            <SkeletonBlock className="is-text" />
          </div>
        ))}
      </article>

      <div className="fmf-skeleton-main" aria-hidden="true">
        <article className="fmf-skeleton-card fmf-skeleton-calendar">
          <div className="fmf-skeleton-section-head">
            <div className="fmf-skeleton-title-group">
              <SkeletonBlock className="is-icon-large" />
              <div>
                <SkeletonBlock className="is-title" />
                <SkeletonBlock className="is-subtitle" />
              </div>
            </div>
            <SkeletonBlock className="is-button" />
          </div>
          <SkeletonBlock className="is-toolbar" />
          <div className="fmf-skeleton-rows">
            {Array.from({ length: 5 }).map((_, index) => (
              <div className="fmf-skeleton-row" key={`row-${index}`}>
                <SkeletonBlock className="is-time" />
                <SkeletonBlock className="is-avatar" />
                <div className="fmf-skeleton-row-copy">
                  <SkeletonBlock className="is-row-title" />
                  <SkeletonBlock className="is-row-subtitle" />
                </div>
                <SkeletonBlock className="is-pill" />
                <SkeletonBlock className="is-number" />
              </div>
            ))}
          </div>
        </article>

        <article className="fmf-skeleton-card fmf-skeleton-insight">
          <div className="fmf-skeleton-section-head">
            <div className="fmf-skeleton-title-group">
              <SkeletonBlock className="is-icon-large" />
              <div>
                <SkeletonBlock className="is-title" />
                <SkeletonBlock className="is-subtitle" />
              </div>
            </div>
          </div>
          <SkeletonBlock className="is-story-title" />
          <SkeletonBlock className="is-story-line" />
          <SkeletonBlock className="is-story-line is-short" />
          <div className="fmf-skeleton-insight-grid">
            {Array.from({ length: 4 }).map((_, index) => (
              <SkeletonBlock className="is-insight-item" key={`insight-${index}`} />
            ))}
          </div>
          <SkeletonBlock className="is-ring" />
        </article>
      </div>

      <div className="fmf-skeleton-tools" aria-hidden="true">
        {Array.from({ length: 3 }).map((_, index) => (
          <article className="fmf-skeleton-card fmf-skeleton-tool" key={`tool-${index}`}>
            <div className="fmf-skeleton-title-group">
              <SkeletonBlock className="is-icon-large" />
              <div>
                <SkeletonBlock className="is-title" />
                <SkeletonBlock className="is-subtitle" />
              </div>
            </div>
            <SkeletonBlock className="is-tool-control" />
            <SkeletonBlock className="is-tool-row" />
            <SkeletonBlock className="is-tool-row" />
            <SkeletonBlock className="is-tool-row" />
          </article>
        ))}
      </div>
    </motion.section>
  );
}

export default function FundamentalAnalysisPage() {
  const initialDashboard = useMemo(() => readCachedDashboard(), []);
  const [data, setData] = useState(initialDashboard.data);
  const [loading, setLoading] = useState(!initialDashboard.hasData);
  const [showInitialSkeleton, setShowInitialSkeleton] = useState(!initialDashboard.hasData);
  const [usingCachedData, setUsingCachedData] = useState(initialDashboard.hasData);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");
  const [query, setQuery] = useState("");
  const [timeZone, setTimeZone] = useState(getInitialTimeZone);
  const [modal, setModal] = useState({ type: null, payload: null });
  const requestInFlightRef = useRef(false);
  const mountedRef = useRef(true);
  const dataRef = useRef(initialDashboard.data);

  setFormattingTimeZone(timeZone);

  const closeModal = useCallback(() => setModal({ type: null, payload: null }), []);
  const openModal = useCallback((type, payload = null) => setModal({ type, payload }), []);
  const updateTimeZone = useCallback((value) => {
    const normalized = isValidTimeZone(value) ? value : DEFAULT_TIME_ZONE;
    setFormattingTimeZone(normalized);
    setTimeZone(normalized);
    if (typeof window !== "undefined") {
      try {
        window.localStorage.setItem(TIME_ZONE_STORAGE_KEY, normalized);
      } catch {
        // Le réglage reste actif pour la session même si le stockage est bloqué.
      }
    }
  }, []);

  const loadDashboard = useCallback(async ({ silent = false } = {}) => {
    if (requestInFlightRef.current) return;
    requestInFlightRef.current = true;

    if (!silent) setRefreshing(true);
    if (!hasDashboardContent(dataRef.current)) setLoading(true);

    try {
      const payload = await withRequestTimeout(
        apiGetFundamentalDashboard({ refresh: !silent }),
      );

      if (payload?.error || payload?.ok === false) {
        throw new Error(
          payload?.error ||
            payload?.msg ||
            "Impossible de charger les données fondamentales.",
        );
      }

      const normalized = normalizeDashboard(payload);
      if (!mountedRef.current) return;

      dataRef.current = normalized;
      setData(normalized);
      writeCachedDashboard(normalized);
      setUsingCachedData(false);
      setError("");
    } catch (requestError) {
      if (!mountedRef.current) return;
      setError(
        requestError?.message ||
          "Impossible de joindre le backend fondamental.",
      );
    } finally {
      requestInFlightRef.current = false;
      if (mountedRef.current) {
        setLoading(false);
        setRefreshing(false);
        setShowInitialSkeleton(false);
      }
    }
  }, []);

  useEffect(() => {
    dataRef.current = data;
  }, [data]);

  useEffect(() => {
    mountedRef.current = true;

    // Le skeleton ne bloque jamais l'interface plus de 650 ms.
    const skeletonTimer = window.setTimeout(() => {
      if (mountedRef.current) setShowInitialSkeleton(false);
    }, INITIAL_SKELETON_MAX_MS);

    loadDashboard({ silent: true });
    const refreshTimer = window.setInterval(
      () => loadDashboard({ silent: true }),
      5 * 60 * 1000,
    );

    return () => {
      mountedRef.current = false;
      window.clearTimeout(skeletonTimer);
      window.clearInterval(refreshTimer);
    };
  }, [loadDashboard]);

  const events = data.economic_events;
  const currencies = data.currency_strength;
  const markets = data.market_bias;
  const alerts = data.alerts;
  const overview = data.overview;
  const insight = data.ai_insight;
  const quick = data.quick_decision;

  const searchResults = useMemo(() => {
    const value = query.trim().toLowerCase();
    if (!value) return [];
    const results = [];
    events.forEach((event) => {
      const haystack = `${event.event} ${event.currency} ${event.category}`.toLowerCase();
      if (haystack.includes(value)) results.push({ type: "event", title: event.event, helper: `${event.currency} · ${formatTime(event.datetime)}`, payload: event });
    });
    currencies.forEach((currency) => {
      const haystack = `${currency.currency} ${currency.summary}`.toLowerCase();
      if (haystack.includes(value)) results.push({ type: "currency", title: currency.currency, helper: `Score ${currency.score} · ${currency.stance}`, payload: currency });
    });
    markets.forEach((market) => {
      const haystack = `${market.symbol} ${market.thesis}`.toLowerCase();
      if (haystack.includes(value)) results.push({ type: "market", title: market.symbol, helper: `${directionLabel(market.direction)} · ${market.confidence}%`, payload: market });
    });
    return results.slice(0, 12);
  }, [query, events, currencies, markets]);

  const selectedEvent = modal.type === "event" ? modal.payload : null;
  const selectedCurrency = modal.type === "currency" ? modal.payload : null;
  const selectedMarket = modal.type === "market" ? modal.payload : null;
  const selectedAlert = modal.type === "alert" ? modal.payload : null;

  const strongest = currencies.find((item) => item.currency === overview.strongest_currency) || currencies[0];
  const weakest = currencies.find((item) => item.currency === overview.weakest_currency) || currencies[currencies.length - 1];

  return (
    <>
      <style>{`
        .fmf-page,.fmf-page *, .fmf-page *::before,.fmf-page *::after{box-sizing:border-box}
        .fmf-page{--bg:#f4f6fb;--card:#fff;--soft:#f8f9fc;--text:#111529;--sub:#526077;--muted:#8994a8;--border:#dfe5ee;--line:#edf0f5;--purple:#7637ee;--purple2:#a347ec;--psoft:#f2edff;--green:#07976a;--gsoft:#ebfaf4;--red:#df3859;--rsoft:#fff0f3;--amber:#d98208;--asoft:#fff7e9;width:100%;min-height:100vh;padding:18px 20px 30px;background:radial-gradient(circle at 35% 0%,rgba(118,55,238,.055),transparent 31%),var(--bg);color:var(--text);font-family:inherit;line-height:1.45;overflow-x:hidden}
        html.dark .fmf-page,body.dark .fmf-page,.dark .fmf-page,[data-theme="dark"] .fmf-page{--bg:#0b0e16;--card:#131824;--soft:#101520;--text:#f7f8fb;--sub:#b2bbcb;--muted:#7f899d;--border:rgba(255,255,255,.09);--line:rgba(255,255,255,.055);--psoft:rgba(118,55,238,.15);--gsoft:rgba(7,151,106,.12);--rsoft:rgba(223,56,89,.12);--asoft:rgba(217,130,8,.12)}
        .fmf-page button,.fmf-page input,.fmf-page select{font:inherit}.fmf-page button{color:inherit}.fmf-page svg:not(.fmf-ring-svg):not(.fmf-spark){width:1em!important;height:1em!important}.fmf-shell{max-width:1740px;margin:auto}.fmf-card{min-width:0;border:1px solid var(--border);border-radius:20px;background:var(--card);box-shadow:0 18px 42px -34px rgba(16,24,40,.36)}
        .fmf-header{display:grid;grid-template-columns:minmax(320px,1fr) minmax(280px,500px) auto;align-items:center;gap:14px;margin-bottom:14px}.fmf-title{display:flex;align-items:center;gap:13px;min-width:0}.fmf-title>span{width:52px;height:52px;display:grid;place-items:center;flex:0 0 52px;border-radius:16px;color:#fff;background:linear-gradient(145deg,var(--purple),var(--purple2));font-size:26px}.fmf-title h1{margin:0 0 4px;font-size:clamp(25px,2vw,34px);letter-spacing:-.045em;line-height:1.1}.fmf-title p{margin:0;color:var(--sub);font-size:12px}
        .fmf-search-trigger{min-height:43px;display:flex;align-items:center;gap:9px;padding:0 12px;border:1px solid var(--border);border-radius:13px;background:var(--card);color:var(--muted)!important;font-size:11px;text-align:left;cursor:pointer}.fmf-search-trigger span{flex:1;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}.fmf-actions{display:flex;gap:8px}.fmf-action{min-height:43px;display:inline-flex;align-items:center;gap:7px;padding:0 13px;border:1px solid var(--border);border-radius:13px;background:var(--card);font-size:11px;font-weight:740;cursor:pointer;white-space:nowrap}.fmf-action.is-primary{border-color:transparent;color:#fff!important;background:var(--purple)}.fmf-action:disabled{opacity:.6;cursor:wait}.fmf-count{min-width:19px;height:19px;display:grid;place-items:center;border-radius:999px;color:#fff;background:var(--red);font-size:8px}.fmf-spin{animation:fmfSpin .8s linear infinite}@keyframes fmfSpin{to{transform:rotate(360deg)}}
        .fmf-error{display:flex;align-items:flex-start;gap:8px;margin-bottom:12px;padding:10px 11px;border:1px solid rgba(217,130,8,.2);border-radius:12px;background:var(--asoft);color:var(--sub);font-size:10px}.fmf-error strong{display:block;color:var(--amber);margin-bottom:2px}.fmf-warning-list{margin:4px 0 0;padding-left:16px}.fmf-loading{min-height:430px;display:grid;place-items:center}.fmf-loading div{display:flex;flex-direction:column;align-items:center;gap:10px;color:var(--muted);font-size:10px}
        .fmf-summary{display:grid;grid-template-columns:repeat(4,minmax(0,1fr));gap:11px;margin-bottom:12px}.fmf-summary-card{min-height:122px;padding:14px;border:1px solid var(--border);border-radius:18px;background:var(--card);text-align:left;cursor:pointer;transition:.15s ease}.fmf-summary-card:hover{border-color:rgba(118,55,238,.25);transform:translateY(-2px)}.fmf-summary-top{display:flex;align-items:center;justify-content:space-between;margin-bottom:10px}.fmf-kicker{display:block;color:var(--muted);font-size:8px;font-weight:800;letter-spacing:.11em;text-transform:uppercase}.fmf-summary-card>strong{display:block;margin-top:5px;font-size:18px}.fmf-summary-foot{display:flex;align-items:center;justify-content:space-between;margin-top:10px;color:var(--sub);font-size:9px}
        .fmf-icon{width:38px;height:38px;display:inline-grid;place-items:center;flex:0 0 38px;border:1px solid;border-radius:12px;font-size:19px}.fmf-icon.is-purple{color:var(--purple);background:var(--psoft);border-color:rgba(118,55,238,.12)}.fmf-icon.is-green{color:var(--green);background:var(--gsoft);border-color:rgba(7,151,106,.13)}.fmf-icon.is-red{color:var(--red);background:var(--rsoft);border-color:rgba(223,56,89,.13)}.fmf-icon.is-amber{color:var(--amber);background:var(--asoft);border-color:rgba(217,130,8,.13)}
        .fmf-direction{display:inline-flex;align-items:center;gap:3px;padding:3px 7px;border-radius:999px;font-size:8px;font-weight:780;white-space:nowrap}.fmf-direction.is-compact{padding:3px 6px;font-size:7px}.fmf-direction.is-up{color:var(--green);background:var(--gsoft)}.fmf-direction.is-down{color:var(--red);background:var(--rsoft)}.fmf-direction.is-neutral{color:var(--muted);background:var(--soft)}
        .fmf-quick{display:grid;grid-template-columns:minmax(0,1.2fr) repeat(4,minmax(140px,.7fr));gap:9px;margin-bottom:12px;padding:13px}.fmf-quick-main{display:flex;align-items:center;gap:11px;padding-right:11px;border-right:1px solid var(--line)}.fmf-quick-main h2{margin:0 0 3px;font-size:14px}.fmf-quick-main p{margin:0;color:var(--purple);font-size:10px;font-weight:760}.fmf-quick-item{padding:8px 10px;border:1px solid var(--line);border-radius:12px;background:var(--soft)}.fmf-quick-item span{display:block;color:var(--muted);font-size:8px}.fmf-quick-item strong{display:block;margin-top:4px;overflow:hidden;font-size:9px;text-overflow:ellipsis;white-space:nowrap}
        .fmf-main-grid{display:grid;grid-template-columns:minmax(0,1.34fr) minmax(360px,.76fr);gap:14px;margin-bottom:14px}.fmf-panel{padding:18px}.fmf-section-head{display:flex;align-items:center;justify-content:space-between;gap:12px;margin-bottom:16px}.fmf-section-title{display:flex;align-items:center;gap:11px;min-width:0}.fmf-section-title h2{margin:0;font-size:17px;letter-spacing:-.02em}.fmf-section-title p{margin:4px 0 0;color:var(--muted);font-size:10px}.fmf-link{display:inline-flex;align-items:center;gap:6px;padding:8px 10px;border:1px solid transparent;border-radius:10px;background:transparent;color:var(--purple)!important;font-size:10px;font-weight:800;cursor:pointer;white-space:nowrap;transition:.15s ease}.fmf-link:hover{border-color:rgba(118,55,238,.16);background:var(--psoft)}
        .fmf-calendar-shell{min-width:0}.fmf-calendar-meta{display:flex;align-items:center;gap:12px;min-height:38px;margin-bottom:10px;padding:7px 10px;border:1px solid var(--line);border-radius:12px;background:var(--soft);color:var(--sub);font-size:9px}.fmf-calendar-total,.fmf-calendar-zone{display:inline-flex;align-items:center;gap:6px;font-weight:750;white-space:nowrap}.fmf-calendar-total{color:var(--text)}.fmf-calendar-zone{margin-left:auto;color:var(--muted)}.fmf-calendar-legend{display:flex;align-items:center;gap:9px}.fmf-calendar-legend span{display:inline-flex;align-items:center;gap:5px;white-space:nowrap}.fmf-calendar-legend i{width:7px;height:7px;border-radius:999px}.fmf-calendar-legend .is-high i{background:var(--red)}.fmf-calendar-legend .is-medium i{background:var(--amber)}.fmf-calendar-legend .is-low i{background:var(--green)}
        .fmf-event-list{width:100%;overflow:hidden;border:1px solid var(--border);border-radius:15px;background:var(--card);box-shadow:0 10px 30px -28px rgba(14,23,45,.45)}.fmf-event-grid{display:grid;grid-template-columns:78px minmax(205px,1.18fr) 82px 82px 88px minmax(220px,1.22fr);align-items:center;gap:10px}.fmf-event-head{min-height:42px;padding:0 14px;background:linear-gradient(180deg,var(--soft),rgba(248,249,252,.58));color:var(--muted);font-size:8px;font-weight:850;letter-spacing:.08em;text-transform:uppercase}.fmf-event-row{position:relative;width:100%;min-height:68px;padding:9px 14px;border:0;border-bottom:1px solid var(--line);background:transparent;text-align:left;cursor:pointer;transition:background .15s ease,transform .15s ease}.fmf-event-row::before{content:"";position:absolute;left:0;top:9px;bottom:9px;width:3px;border-radius:0 999px 999px 0;background:var(--green);opacity:.88}.fmf-event-row.is-fort::before{background:var(--red)}.fmf-event-row.is-moyen::before{background:var(--amber)}.fmf-event-row:last-child{border-bottom:0}.fmf-event-row:hover{z-index:1;background:linear-gradient(90deg,var(--psoft),transparent);transform:translateX(2px)}.fmf-event-when{display:flex;flex-direction:column;gap:3px}.fmf-event-when strong{font-size:12px;line-height:1;color:var(--text)}.fmf-event-when small{color:var(--muted);font-size:7px;font-weight:650;text-transform:capitalize}.fmf-event-name{display:flex;align-items:center;gap:10px;min-width:0}.fmf-event-flag{width:34px;height:34px;display:grid;place-items:center;flex:0 0 34px;border:1px solid var(--line);border-radius:10px;background:var(--soft);font-size:18px;box-shadow:0 4px 10px -8px rgba(0,0,0,.4)}.fmf-event-name>span{min-width:0}.fmf-event-name strong,.fmf-event-name small{display:block;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}.fmf-event-name strong{color:var(--text);font-size:11px;line-height:1.25}.fmf-event-name small{margin-top:4px;color:var(--muted);font-size:8px}.fmf-event-name small em{display:inline-flex;margin-right:6px;padding:2px 5px;border-radius:5px;background:var(--psoft);color:var(--purple);font-style:normal;font-size:7px;font-weight:850}.fmf-event-impact{display:flex;align-items:center}.fmf-event-value{display:flex;flex-direction:column;gap:3px}.fmf-event-value small{display:none;color:var(--muted);font-size:7px}.fmf-event-value strong{font-size:10px;color:var(--text)}.fmf-event-actual strong{display:inline-flex;align-items:center;width:max-content;min-height:25px;padding:4px 8px;border-radius:8px;background:var(--soft)}.fmf-event-actual.is-positive strong{color:var(--green);background:var(--gsoft)}.fmf-event-actual.is-negative strong{color:var(--red);background:var(--rsoft)}.fmf-event-actual.is-neutral strong{color:var(--text)}.fmf-event-actual.is-pending strong{color:var(--muted);background:var(--soft)}.fmf-event-reading{display:flex;align-items:center;justify-content:space-between;gap:10px;min-width:0;color:var(--sub);font-size:9px;line-height:1.4}.fmf-event-reading>span{display:-webkit-box;overflow:hidden;-webkit-line-clamp:2;-webkit-box-orient:vertical}.fmf-event-reading>i{width:29px;height:29px;display:grid;place-items:center;flex:0 0 29px;border:1px solid var(--line);border-radius:9px;background:var(--soft);color:var(--purple);font-style:normal;transition:.15s ease}.fmf-event-row:hover .fmf-event-reading>i{border-color:rgba(118,55,238,.2);background:var(--psoft)}
        .fmf-pill{min-width:50px;justify-content:center;padding:5px 9px;font-size:8px;letter-spacing:.01em}
        .fmf-pill{display:inline-flex;padding:3px 7px;border-radius:999px;font-size:7px;font-weight:780}.fmf-pill.is-fort,.fmf-pill.is-negative{color:var(--red);background:var(--rsoft)}.fmf-pill.is-moyen,.fmf-pill.is-warning{color:var(--amber);background:var(--asoft)}.fmf-pill.is-faible,.fmf-pill.is-positive{color:var(--green);background:var(--gsoft)}.fmf-pill.is-neutral{color:var(--sub);background:var(--soft)}.fmf-text-positive{color:var(--green)!important}.fmf-text-negative{color:var(--red)!important}.fmf-text-neutral{color:var(--muted)!important}
        .fmf-insight{background:linear-gradient(145deg,var(--card),var(--psoft));border-color:rgba(118,55,238,.22)}.fmf-insight-layout{display:grid;grid-template-columns:minmax(0,1fr) 118px;gap:18px;align-items:center}.fmf-insight h3{margin:0 0 6px;font-size:16px}.fmf-insight-copy>p{margin:0;color:var(--sub);font-size:10px;line-height:1.65}.fmf-reasons{display:grid;grid-template-columns:1fr 1fr;gap:7px;margin-top:12px}.fmf-reasons div{display:flex;align-items:flex-start;gap:6px;padding:7px 8px;border:1px solid var(--line);border-radius:10px;background:var(--soft);color:var(--sub);font-size:8px}.fmf-reasons svg{color:var(--purple);margin-top:1px}.fmf-bias{display:flex;gap:7px;margin-top:12px;padding:9px 10px;border:1px solid rgba(118,55,238,.15);border-radius:11px;background:var(--psoft);color:var(--purple);font-size:8px}.fmf-bias span{color:var(--sub)}
        .fmf-ring-wrap{display:flex;flex-direction:column;align-items:center;gap:4px}.fmf-ring-wrap>span{color:var(--muted);font-size:8px}.fmf-ring{position:relative;width:110px;height:110px}.fmf-ring-svg{width:110px!important;height:110px!important}.fmf-ring-bg{stroke:var(--line)}.fmf-ring>strong{position:absolute;inset:0;display:grid;place-items:center;font-size:21px}
        .fmf-secondary{display:grid;grid-template-columns:minmax(310px,.72fr) minmax(0,1.28fr);gap:12px;margin-bottom:12px}.fmf-currencies{display:flex;flex-direction:column;gap:5px}.fmf-currency{display:grid;grid-template-columns:20px 22px 52px minmax(90px,1fr) 30px 52px;align-items:center;gap:7px;padding:6px 7px;border:1px solid transparent;border-radius:10px;background:transparent;text-align:left;cursor:pointer}.fmf-currency:hover{border-color:var(--border);background:var(--soft)}.fmf-currency>span:first-child{color:var(--muted);font-size:8px}.fmf-currency>span:nth-child(2){font-size:14px}.fmf-currency>strong{font-size:9px}.fmf-track{height:6px;overflow:hidden;border-radius:999px;background:var(--line)}.fmf-track i{display:block;height:100%;border-radius:inherit;background:linear-gradient(90deg,var(--purple),#d84bdc)}.fmf-currency>b{font-size:8px;text-align:right}.fmf-change{display:flex;align-items:center;justify-content:flex-end;gap:2px;font-size:8px;font-weight:780}.fmf-change.is-up{color:var(--green)}.fmf-change.is-down{color:var(--red)}.fmf-change.is-neutral{color:var(--muted)}
        .fmf-markets{display:grid;grid-template-columns:repeat(5,minmax(0,1fr));gap:8px}.fmf-market{min-width:0;padding:11px;border:1px solid var(--border);border-radius:14px;background:var(--soft);text-align:left;cursor:pointer;transition:.15s ease}.fmf-market:hover{border-color:rgba(118,55,238,.24);transform:translateY(-2px)}.fmf-market header{display:flex;justify-content:space-between;gap:8px}.fmf-market h3{margin:0;font-size:10px}.fmf-market>.fmf-direction{margin-top:10px}.fmf-spark{display:block!important;width:100%!important;height:33px!important;margin:8px 0 4px;color:var(--muted)}.fmf-spark.is-up{color:var(--green)}.fmf-spark.is-down{color:var(--red)}.fmf-market footer{display:flex;align-items:flex-end;justify-content:space-between}.fmf-market footer span,.fmf-market footer strong{display:block}.fmf-market footer span{color:var(--muted);font-size:7px}.fmf-market footer strong{color:var(--text);font-size:11px}
        .fmf-tools{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:12px;margin-bottom:12px}.fmf-tool-card{padding:16px}.fmf-compare-selects{display:grid;grid-template-columns:1fr 35px 1fr;align-items:end;gap:8px}.fmf-compare-selects label>span{display:block;margin-bottom:5px;color:var(--muted);font-size:7px;text-transform:uppercase}.fmf-compare-selects label>div,.fmf-select{position:relative;display:flex;align-items:center;min-height:38px;padding:0 28px 0 10px;border:1px solid var(--border);border-radius:11px;background:var(--soft)}.fmf-compare-selects select,.fmf-select select{width:100%;appearance:none;border:0;outline:0;background:transparent;color:var(--text);font-size:9px;font-weight:700}.fmf-compare-selects svg,.fmf-select svg{position:absolute;right:9px;color:var(--muted)}.fmf-compare-selects>i{width:35px;height:35px;display:grid;place-items:center;border-radius:10px;color:var(--purple);background:var(--psoft);font-size:8px;font-style:normal;font-weight:850}.fmf-compare-result{display:grid;grid-template-columns:1fr 1.1fr 1fr;gap:7px;margin-top:10px}.fmf-compare-result>button,.fmf-compare-result>div{padding:10px;border:1px solid var(--line);border-radius:11px;background:var(--soft);text-align:center}.fmf-compare-result>button{cursor:pointer}.fmf-compare-result span,.fmf-compare-result strong,.fmf-compare-result b,.fmf-compare-result small{display:block}.fmf-compare-result button span{font-size:18px}.fmf-compare-result button strong{font-size:9px}.fmf-compare-result button b{color:var(--purple);font-size:14px}.fmf-compare-result div span,.fmf-compare-result div small{color:var(--muted);font-size:7px}.fmf-compare-result div strong{margin:6px 0;font-size:14px}.fmf-select{margin-bottom:9px}
        .fmf-impact-list{display:flex;flex-direction:column;gap:6px}.fmf-impact-list>div{display:flex;align-items:center;justify-content:space-between;gap:8px;padding:8px 9px;border:1px solid var(--line);border-radius:10px;background:var(--soft)}.fmf-impact-list strong,.fmf-impact-list small{display:block}.fmf-impact-list strong{font-size:9px}.fmf-impact-list small{max-width:220px;overflow:hidden;color:var(--muted);font-size:7px;text-overflow:ellipsis;white-space:nowrap}.fmf-tool-card>.fmf-link{margin-top:10px}.fmf-risk-list{display:flex;flex-direction:column;gap:5px}.fmf-risk-list button{display:grid;grid-template-columns:7px 42px minmax(0,1fr) 14px;align-items:center;gap:8px;padding:8px;border:1px solid transparent;border-radius:10px;background:transparent;text-align:left;cursor:pointer}.fmf-risk-list button:hover{border-color:var(--border);background:var(--soft)}.fmf-risk-list i{width:7px;height:32px;border-radius:999px;background:var(--green)}.fmf-risk-list i.is-fort{background:var(--red)}.fmf-risk-list i.is-moyen{background:var(--amber)}.fmf-risk-list time{font-size:9px;font-weight:780}.fmf-risk-list strong,.fmf-risk-list small{display:block}.fmf-risk-list strong{font-size:8px}.fmf-risk-list small{color:var(--muted);font-size:7px}
        .fmf-alert-grid{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:9px}.fmf-alert{display:grid;grid-template-columns:38px minmax(0,1fr) auto;gap:9px;padding:11px;border:1px solid var(--line);border-radius:13px;background:var(--soft);text-align:left;cursor:pointer}.fmf-alert>span:first-child{width:38px;height:38px;display:grid;place-items:center;border-radius:11px;color:var(--amber);background:var(--asoft);font-size:17px}.fmf-alert.is-critical>span:first-child{color:var(--red);background:var(--rsoft)}.fmf-alert.is-opportunity>span:first-child{color:var(--green);background:var(--gsoft)}.fmf-alert strong,.fmf-alert small{display:block}.fmf-alert strong{font-size:9px}.fmf-alert small{color:var(--sub);font-size:7px}.fmf-alert>b{color:var(--muted);font-size:7px;white-space:nowrap}
        .fmf-empty{min-height:130px;display:flex;flex-direction:column;align-items:center;justify-content:center;padding:18px;color:var(--muted);text-align:center}.fmf-empty svg{font-size:22px}.fmf-empty strong{margin-top:8px;color:var(--text);font-size:10px}.fmf-empty span{margin-top:3px;font-size:8px}
        .fmf-backdrop{position:fixed;inset:0;z-index:9999;display:grid;place-items:center;padding:20px;background:rgba(8,11,20,.55);backdrop-filter:blur(7px)}.fmf-modal{width:min(100%,720px);max-height:88vh;overflow:hidden;display:flex;flex-direction:column;border:1px solid var(--border);border-radius:22px;background:var(--card);box-shadow:0 30px 80px rgba(0,0,0,.28)}.fmf-modal.is-large{width:min(100%,1120px)}.fmf-modal.is-small{width:min(100%,520px)}.fmf-modal>header{display:flex;justify-content:space-between;gap:14px;padding:16px 18px;border-bottom:1px solid var(--line)}.fmf-modal h2{margin:0;font-size:17px}.fmf-modal header p{margin:3px 0 0;color:var(--muted);font-size:9px}.fmf-modal header button{width:35px;height:35px;display:grid;place-items:center;border:1px solid var(--border);border-radius:11px;background:var(--soft);cursor:pointer}.fmf-modal-body{overflow-y:auto;padding:17px 18px 19px}.fmf-modal-grid{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:8px}.fmf-stat{padding:10px;border:1px solid var(--line);border-radius:11px;background:var(--soft)}.fmf-stat span,.fmf-stat strong{display:block}.fmf-stat span{color:var(--muted);font-size:7px;text-transform:uppercase}.fmf-stat strong{margin-top:4px;font-size:11px}.fmf-callout{display:flex;gap:8px;margin-top:10px;padding:11px;border:1px solid rgba(118,55,238,.14);border-radius:12px;background:var(--psoft);color:var(--purple)}.fmf-callout span{color:var(--sub);font-size:9px}.fmf-subtitle{margin:15px 0 8px;font-size:11px}.fmf-tags{display:flex;flex-wrap:wrap;gap:5px}.fmf-tags span{padding:4px 8px;border:1px solid var(--border);border-radius:999px;background:var(--soft);font-size:8px}.fmf-breakdown{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:8px}.fmf-breakdown>div{padding:10px;border:1px solid var(--line);border-radius:11px;background:var(--soft)}.fmf-breakdown header{display:flex;justify-content:space-between;margin-bottom:7px}.fmf-breakdown span{font-size:8px}.fmf-breakdown strong{font-size:9px}.fmf-market-detail{display:grid;grid-template-columns:1fr 120px;gap:16px}.fmf-market-detail h3{margin:8px 0 5px;font-size:20px}.fmf-market-detail p{margin:0;color:var(--sub);font-size:9px}.fmf-detail-list{display:flex;flex-direction:column;gap:7px;margin-top:11px}.fmf-detail-list div{display:flex;gap:7px;padding:9px;border:1px solid var(--line);border-radius:10px;background:var(--soft);font-size:8px}
        .fmf-modal-search{display:flex;align-items:center;gap:8px;min-height:44px;padding:0 11px;border:1px solid var(--border);border-radius:12px;background:var(--soft)}.fmf-modal-search input{width:100%;border:0;outline:0;background:transparent;color:var(--text);font-size:10px}.fmf-modal-search button{border:0;background:transparent;cursor:pointer}.fmf-results{display:flex;flex-direction:column;gap:6px;margin-top:10px}.fmf-results>button{display:grid;grid-template-columns:38px minmax(0,1fr) 15px;align-items:center;gap:9px;padding:9px;border:1px solid var(--line);border-radius:11px;background:var(--soft);text-align:left;cursor:pointer}.fmf-results strong,.fmf-results small{display:block}.fmf-results strong{font-size:9px}.fmf-results small{color:var(--muted);font-size:7px}

        /* ===== Premium modal system ===== */
        .fmf-backdrop{padding:24px;background:rgba(10,14,26,.64);backdrop-filter:blur(12px) saturate(120%);animation:fmfFadeIn .18s ease-out}
        .fmf-modal{width:min(100%,780px);max-height:min(91vh,900px);border:1px solid var(--border);border-radius:26px;background:linear-gradient(180deg,var(--card),var(--soft));box-shadow:0 36px 110px rgba(5,9,20,.36),0 0 0 1px rgba(255,255,255,.35) inset;animation:fmfModalIn .22s cubic-bezier(.2,.8,.2,1)}
        .fmf-modal.is-large{width:min(100%,1220px)}.fmf-modal.is-event{width:min(100%,980px)}
        .fmf-modal-header{position:relative;display:flex;align-items:flex-start;justify-content:space-between;gap:18px;padding:23px 26px 20px;border-bottom:1px solid var(--line);background:linear-gradient(135deg,var(--psoft),transparent 58%)}
        .fmf-modal-heading{min-width:0}.fmf-modal-eyebrow{display:block;margin-bottom:7px;color:var(--purple);font-size:8px;font-weight:850;letter-spacing:.14em;text-transform:uppercase}.fmf-modal h2{margin:0;font-size:23px;line-height:1.18;letter-spacing:-.025em}.fmf-modal-header p{margin:7px 0 0;color:var(--muted);font-size:11px}.fmf-modal-close{width:44px!important;height:44px!important;flex:0 0 44px;border:1px solid var(--border)!important;border-radius:14px!important;background:var(--soft)!important;font-size:18px;transition:.16s ease}.fmf-modal-close:hover{color:#fff;background:var(--purple)!important;border-color:var(--purple)!important;transform:rotate(4deg)}
        .fmf-modal-body{padding:24px 26px 28px;scrollbar-width:thin;scrollbar-color:rgba(118,55,238,.42) transparent}.fmf-modal-body::-webkit-scrollbar{width:7px}.fmf-modal-body::-webkit-scrollbar-thumb{border-radius:999px;background:rgba(118,55,238,.38)}
        @keyframes fmfFadeIn{from{opacity:0}to{opacity:1}}@keyframes fmfModalIn{from{opacity:0;transform:translateY(14px) scale(.985)}to{opacity:1;transform:none}}

        /* ===== Premium economic-event popup ===== */
        .fmf-event-detail{display:flex;flex-direction:column;gap:18px}.fmf-event-hero{display:flex;align-items:center;justify-content:space-between;gap:18px;padding:17px 18px;border:1px solid var(--border);border-radius:18px;background:linear-gradient(135deg,var(--soft),var(--card));position:relative;overflow:hidden}.fmf-event-hero::before{content:"";position:absolute;inset:0 auto 0 0;width:5px;background:var(--green)}.fmf-event-hero.is-fort::before{background:var(--red)}.fmf-event-hero.is-moyen::before{background:var(--amber)}
        .fmf-event-hero-main{display:flex;align-items:center;gap:13px;min-width:0}.fmf-event-hero-flag{width:54px;height:54px;display:grid;place-items:center;flex:0 0 54px;border:1px solid var(--border);border-radius:16px;background:var(--card);font-size:27px;box-shadow:0 8px 22px rgba(15,23,42,.07)}.fmf-event-hero-main>div{min-width:0}.fmf-event-hero-code{display:inline-flex;margin-bottom:3px;color:var(--purple);font-size:10px;font-weight:850;letter-spacing:.12em}.fmf-event-hero-main strong{display:block;font-size:15px}.fmf-event-hero-main small{display:flex;align-items:center;gap:5px;margin-top:5px;color:var(--muted);font-size:10px}.fmf-event-hero-status{display:flex;align-items:flex-end;flex-direction:column;gap:9px}.fmf-publication-status{display:inline-flex;align-items:center;gap:6px;color:var(--muted);font-size:9px;font-weight:750}.fmf-publication-status i{width:7px;height:7px;border-radius:50%;background:var(--amber);box-shadow:0 0 0 4px var(--asoft)}.fmf-publication-status.is-published{color:var(--green)}.fmf-publication-status.is-published i{background:var(--green);box-shadow:0 0 0 4px var(--gsoft)}
        .fmf-event-kpis{display:grid;grid-template-columns:repeat(5,minmax(0,1fr));gap:10px}.fmf-event-kpis>div{min-width:0;padding:14px;border:1px solid var(--border);border-radius:15px;background:var(--soft);transition:.16s ease}.fmf-event-kpis>div:hover{border-color:rgba(118,55,238,.22);transform:translateY(-1px)}.fmf-event-kpis span,.fmf-event-kpis strong,.fmf-event-kpis small{display:block}.fmf-event-kpis span{color:var(--muted);font-size:8px;font-weight:800;letter-spacing:.07em;text-transform:uppercase}.fmf-event-kpis strong{margin-top:7px;font-size:16px}.fmf-event-kpis small{margin-top:4px;color:var(--muted);font-size:8px;line-height:1.35}.fmf-event-kpis>div.is-positive{border-color:rgba(7,151,106,.2);background:var(--gsoft)}.fmf-event-kpis>div.is-negative{border-color:rgba(223,56,89,.2);background:var(--rsoft)}
        .fmf-trader-reading{display:grid;grid-template-columns:46px minmax(0,1fr);gap:13px;align-items:start;padding:16px 17px;border:1px solid rgba(118,55,238,.2);border-radius:17px;background:linear-gradient(135deg,var(--psoft),var(--card))}.fmf-trader-reading-icon{width:46px;height:46px;display:grid;place-items:center;border-radius:14px;color:#fff;background:linear-gradient(145deg,var(--purple),var(--purple2));font-size:20px;box-shadow:0 10px 22px rgba(118,55,238,.22)}.fmf-trader-reading span,.fmf-trader-reading strong,.fmf-trader-reading small{display:block}.fmf-trader-reading>div>span{color:var(--purple);font-size:9px;font-weight:850;letter-spacing:.08em;text-transform:uppercase}.fmf-trader-reading strong{margin-top:5px;font-size:13px;line-height:1.5}.fmf-trader-reading small{margin-top:5px;color:var(--muted);font-size:9px}
        .fmf-detail-section-title{display:flex;align-items:flex-end;justify-content:space-between;gap:12px}.fmf-detail-section-title h3{margin:0;font-size:15px}.fmf-detail-section-title p{margin:4px 0 0;color:var(--muted);font-size:9px}.fmf-detail-section-title>span{padding:5px 9px;border-radius:999px;color:var(--purple);background:var(--psoft);font-size:8px;font-weight:800}
        .fmf-event-assets{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:10px}.fmf-event-asset{padding:14px;border:1px solid var(--border);border-radius:16px;background:var(--soft);position:relative;overflow:hidden}.fmf-event-asset::before{content:"";position:absolute;inset:0 auto 0 0;width:3px;background:var(--muted)}.fmf-event-asset.is-up::before{background:var(--green)}.fmf-event-asset.is-down::before{background:var(--red)}.fmf-event-asset header{display:flex;align-items:flex-start;justify-content:space-between;gap:10px}.fmf-event-asset header span,.fmf-event-asset header strong{display:block}.fmf-event-asset header span{color:var(--muted);font-size:7px;font-weight:800;letter-spacing:.08em}.fmf-event-asset header strong{margin-top:3px;font-size:13px}.fmf-event-asset p{min-height:34px;margin:10px 0;color:var(--sub);font-size:9px;line-height:1.5}.fmf-event-asset footer{display:grid;grid-template-columns:auto minmax(70px,1fr) auto;align-items:center;gap:8px}.fmf-event-asset footer span{color:var(--muted);font-size:8px}.fmf-event-asset footer strong{font-size:9px}.fmf-event-confidence{height:6px;overflow:hidden;border-radius:999px;background:var(--line)}.fmf-event-confidence i{display:block;height:100%;border-radius:inherit;background:linear-gradient(90deg,var(--purple),var(--purple2))}.fmf-risk-note{display:flex;align-items:flex-start;gap:10px;padding:13px 14px;border:1px solid rgba(217,130,8,.18);border-radius:14px;background:var(--asoft);color:var(--amber)}.fmf-risk-note>div strong,.fmf-risk-note>div span{display:block}.fmf-risk-note strong{font-size:10px}.fmf-risk-note span{margin-top:2px;color:var(--sub);font-size:9px}

        /* ===== Premium Forex comparator ===== */
        .fmf-tools{align-items:start}.fmf-tool-card{min-height:0}.fmf-comparator-card{padding:18px;background:linear-gradient(165deg,var(--card),var(--soft));border-color:rgba(118,55,238,.16)}.fmf-premium-label{display:inline-flex;align-items:center;gap:5px;padding:5px 8px;border-radius:999px;color:var(--purple);background:var(--psoft);font-size:7px;font-weight:800;white-space:nowrap}
        .fmf-compare-toolbar{display:grid;grid-template-columns:minmax(0,1fr) 42px minmax(0,1fr);align-items:end;gap:10px}.fmf-compare-toolbar label>span{display:block;margin-bottom:6px;color:var(--muted);font-size:8px;font-weight:800;letter-spacing:.06em;text-transform:uppercase}.fmf-compare-toolbar label>div{position:relative;min-height:46px;display:grid;grid-template-columns:26px minmax(0,1fr) 16px;align-items:center;gap:7px;padding:0 11px;border:1px solid var(--border);border-radius:13px;background:var(--card);transition:.15s ease}.fmf-compare-toolbar label>div:focus-within{border-color:rgba(118,55,238,.5);box-shadow:0 0 0 3px rgba(118,55,238,.08)}.fmf-compare-toolbar label b{font-size:18px}.fmf-compare-toolbar select{width:100%;appearance:none;border:0;outline:0;background:transparent;color:var(--text);font-size:12px;font-weight:800}.fmf-compare-toolbar svg{color:var(--muted)}.fmf-swap-button{width:42px;height:42px;display:grid;place-items:center;border:1px solid rgba(118,55,238,.16);border-radius:13px;background:var(--psoft);color:var(--purple)!important;cursor:pointer;transition:.16s ease}.fmf-swap-button:hover{color:#fff!important;background:var(--purple);transform:rotate(180deg)}
        .fmf-compare-stage{display:grid;grid-template-columns:minmax(0,1fr) minmax(145px,.9fr) minmax(0,1fr);align-items:stretch;gap:10px;margin-top:12px}.fmf-compare-currency{position:relative;display:grid;grid-template-columns:40px minmax(0,1fr) auto;align-items:center;gap:10px;padding:13px;border:1px solid var(--border);border-radius:16px;background:var(--card);text-align:left;cursor:pointer;overflow:hidden;transition:.16s ease}.fmf-compare-currency:hover{border-color:rgba(118,55,238,.28);transform:translateY(-2px);box-shadow:0 10px 24px rgba(24,30,54,.07)}.fmf-compare-flag{width:40px;height:40px;display:grid;place-items:center;border-radius:12px;background:var(--soft);font-size:21px}.fmf-compare-currency div span,.fmf-compare-currency div strong,.fmf-compare-currency div small{display:block}.fmf-compare-currency div span{color:var(--muted);font-size:7px;text-transform:uppercase}.fmf-compare-currency div strong{margin-top:2px;font-size:14px}.fmf-compare-currency div small{margin-top:2px;color:var(--sub);font-size:8px}.fmf-compare-currency>b{color:var(--purple);font-size:20px}.fmf-score-track{position:absolute;inset:auto 13px 10px 13px;height:4px;overflow:hidden;border-radius:999px;background:var(--line)}.fmf-score-track i{display:block;height:100%;border-radius:inherit;background:linear-gradient(90deg,var(--purple),var(--purple2))}
        .fmf-compare-center{display:flex;flex-direction:column;align-items:center;justify-content:center;padding:12px 9px;border:1px solid rgba(118,55,238,.14);border-radius:16px;background:var(--psoft);text-align:center}.fmf-edge-badge{padding:4px 7px;border-radius:999px;background:var(--card);color:var(--muted);font-size:7px;font-weight:850;text-transform:uppercase}.fmf-edge-badge.is-up{color:var(--green)}.fmf-edge-badge.is-down{color:var(--red)}.fmf-compare-center>strong{margin:7px 0 5px;font-size:15px}.fmf-balance-meter{width:100%;height:7px;display:flex;overflow:hidden;margin-top:9px;border-radius:999px;background:var(--line)}.fmf-balance-meter .is-left{height:100%;background:linear-gradient(90deg,var(--purple),var(--purple2))}.fmf-balance-meter .is-right{height:100%;background:linear-gradient(90deg,#9ba6bb,#c5cbd6)}.fmf-gap-value{margin-top:8px;font-size:15px;font-weight:850}.fmf-compare-center>small{margin-top:2px;color:var(--muted);font-size:7px}
        .fmf-forex-readout{display:grid;grid-template-columns:38px minmax(0,1fr) auto;align-items:center;gap:10px;margin-top:12px;padding:12px 13px;border:1px solid var(--border);border-radius:15px;background:var(--soft)}.fmf-forex-readout>span{width:38px;height:38px;display:grid;place-items:center;border-radius:12px;color:var(--purple);background:var(--psoft);font-size:18px}.fmf-forex-readout small,.fmf-forex-readout strong,.fmf-forex-readout p{display:block}.fmf-forex-readout small{color:var(--muted);font-size:7px;font-weight:800;text-transform:uppercase}.fmf-forex-readout strong{margin-top:2px;font-size:10px}.fmf-forex-readout p{margin:3px 0 0;color:var(--sub);font-size:8px}.fmf-forex-readout>b{color:var(--purple);font-size:16px}.fmf-forex-readout.is-up{border-color:rgba(7,151,106,.17);background:var(--gsoft)}.fmf-forex-readout.is-down{border-color:rgba(223,56,89,.17);background:var(--rsoft)}


        .fmf-trade-status{display:flex;align-items:center;gap:12px;margin:14px 0;padding:13px 14px;border:1px solid var(--border);border-radius:14px;background:var(--soft)}
        .fmf-trade-status>span{width:38px;height:38px;display:grid;place-items:center;flex:0 0 38px;border-radius:11px;color:var(--purple);background:var(--psoft);font-size:18px}
        .fmf-trade-status small,.fmf-trade-status strong{display:block}.fmf-trade-status small{color:var(--muted);font-size:8px;text-transform:uppercase;letter-spacing:.05em}.fmf-trade-status strong{margin-top:3px;font-size:11px;line-height:1.45}
        .fmf-trade-status.is-blocked{border-color:rgba(223,56,89,.22);background:var(--rsoft)}.fmf-trade-status.is-blocked>span{color:var(--red);background:rgba(223,56,89,.1)}
        .fmf-trade-status.is-prepare,.fmf-trade-status.is-caution{border-color:rgba(217,130,8,.22);background:var(--asoft)}.fmf-trade-status.is-prepare>span,.fmf-trade-status.is-caution>span{color:var(--amber);background:rgba(217,130,8,.1)}
        .fmf-trade-status.is-wait_confirmation{border-color:rgba(7,151,106,.22);background:var(--gsoft)}.fmf-trade-status.is-wait_confirmation>span{color:var(--green);background:rgba(7,151,106,.1)}
        .fmf-understand-grid{display:grid;grid-template-columns:1.2fr 1fr 1fr;gap:9px;margin:14px 0}
        .fmf-understand-grid article{display:flex;align-items:flex-start;gap:9px;padding:12px;border:1px solid var(--line);border-radius:13px;background:var(--soft)}
        .fmf-understand-grid article>span{width:31px;height:31px;display:grid;place-items:center;flex:0 0 31px;border-radius:9px;color:var(--purple);background:var(--psoft)}
        .fmf-understand-grid article.is-positive>span{color:var(--green);background:var(--gsoft)}.fmf-understand-grid article.is-negative>span{color:var(--red);background:var(--rsoft)}
        .fmf-understand-grid small,.fmf-understand-grid strong{display:block}.fmf-understand-grid small{color:var(--muted);font-size:7px;text-transform:uppercase;letter-spacing:.04em}.fmf-understand-grid strong{margin-top:4px;font-size:9px;line-height:1.5}
        .fmf-asset-action{display:block;margin-top:8px;padding-top:7px;border-top:1px dashed var(--line);color:var(--purple);font-size:8px;font-weight:700}
        .fmf-checklist{margin-top:14px;padding:13px;border:1px solid rgba(118,55,238,.16);border-radius:14px;background:var(--psoft)}
        .fmf-checklist header{display:flex;align-items:center;gap:7px;color:var(--purple);font-size:10px}.fmf-checklist>div{display:grid;grid-template-columns:repeat(3,1fr);gap:7px;margin-top:10px}
        .fmf-checklist>div span{display:flex;align-items:flex-start;gap:6px;padding:9px;border:1px solid var(--line);border-radius:10px;background:var(--card);color:var(--sub);font-size:8px;line-height:1.45}.fmf-checklist>div svg{flex:0 0 auto;color:var(--green);margin-top:1px}
        .fmf-pair-rule,.fmf-score-help{display:flex;align-items:flex-start;gap:8px;margin:10px 0;padding:10px 11px;border:1px solid rgba(118,55,238,.15);border-radius:11px;background:var(--psoft);color:var(--purple);font-size:8px;line-height:1.5}
        .fmf-pair-rule span,.fmf-score-help span{color:var(--sub)}.fmf-pair-rule svg,.fmf-score-help svg{flex:0 0 auto;margin-top:2px}
        .fmf-compare-currency em{display:block;margin-top:7px;color:var(--muted);font-size:7px;font-style:normal}

        @media(max-width:1300px){.fmf-header{grid-template-columns:1fr minmax(280px,440px)}.fmf-actions{grid-column:2;justify-content:flex-end}.fmf-quick{grid-template-columns:1fr 1fr 1fr}.fmf-quick-main{grid-column:1/-1;padding-bottom:10px;border-right:0;border-bottom:1px solid var(--line)}.fmf-main-grid,.fmf-secondary{grid-template-columns:1fr}.fmf-markets{grid-template-columns:repeat(5,minmax(145px,1fr));overflow-x:auto;padding-bottom:3px}.fmf-tools{grid-template-columns:1fr 1fr}.fmf-tools>:last-child{grid-column:1/-1}}
        @media(max-width:900px){.fmf-understand-grid{grid-template-columns:1fr}.fmf-checklist>div{grid-template-columns:1fr}.fmf-page{padding:12px}.fmf-header{grid-template-columns:1fr}.fmf-actions{grid-column:auto;justify-content:flex-start}.fmf-summary{grid-template-columns:1fr 1fr}.fmf-tools{grid-template-columns:1fr}.fmf-tools>:last-child{grid-column:auto}.fmf-calendar-meta{flex-wrap:wrap}.fmf-calendar-zone{margin-left:0}.fmf-event-head{display:none}.fmf-event-grid{grid-template-columns:74px minmax(0,1fr) auto;gap:9px}.fmf-event-row{min-height:92px;padding:12px 12px 12px 15px}.fmf-event-row::before{top:12px;bottom:12px}.fmf-event-when{grid-column:1;grid-row:1}.fmf-event-name{grid-column:2;grid-row:1}.fmf-event-impact{grid-column:3;grid-row:1;justify-content:flex-end}.fmf-event-value{grid-row:2}.fmf-event-row>.fmf-event-value:nth-of-type(4){grid-column:1}.fmf-event-row>.fmf-event-value:nth-of-type(5){grid-column:2}.fmf-event-value small{display:block}.fmf-event-reading{grid-column:2/4;grid-row:3;margin-top:2px}.fmf-event-reading>span{-webkit-line-clamp:2}.fmf-reasons{grid-template-columns:1fr}}
        @media(max-width:900px){.fmf-event-kpis{grid-template-columns:repeat(3,minmax(0,1fr))}.fmf-event-assets{grid-template-columns:1fr}.fmf-compare-stage{grid-template-columns:1fr}.fmf-compare-center{order:-1}.fmf-comparator-card{padding:15px}}
        @media(max-width:700px){.fmf-modal-header{padding:18px}.fmf-modal-body{padding:18px}.fmf-modal h2{font-size:19px}.fmf-event-hero{align-items:flex-start;flex-direction:column}.fmf-event-hero-status{align-items:flex-start}.fmf-event-kpis{grid-template-columns:1fr 1fr}.fmf-compare-toolbar{grid-template-columns:1fr 42px 1fr}.fmf-event-assets{grid-template-columns:1fr}}
        @media(max-width:620px){.fmf-page{padding:9px}.fmf-title>span{width:46px;height:46px;flex-basis:46px}.fmf-title h1{font-size:24px}.fmf-summary{grid-template-columns:1fr}.fmf-actions{display:grid;grid-template-columns:1fr 1fr;width:100%}.fmf-quick{grid-template-columns:1fr}.fmf-insight-layout{grid-template-columns:1fr}.fmf-ring-wrap{align-items:flex-start}.fmf-markets{grid-template-columns:repeat(5,145px)}.fmf-alert-grid{grid-template-columns:1fr}.fmf-compare-selects,.fmf-compare-result,.fmf-modal-grid,.fmf-breakdown,.fmf-market-detail{grid-template-columns:1fr}.fmf-compare-selects>i{margin:auto}.fmf-backdrop{padding:8px}.fmf-modal{max-height:94vh;border-radius:17px}.fmf-modal-body{padding:14px}.fmf-section-head{align-items:flex-start}.fmf-section-head>.fmf-link{margin-top:10px}.fmf-calendar-legend{width:100%;justify-content:space-between}.fmf-calendar-zone{display:none}.fmf-event-grid{grid-template-columns:62px minmax(0,1fr) auto}.fmf-event-row{min-height:112px}.fmf-event-flag{width:30px;height:30px;flex-basis:30px;font-size:16px}.fmf-event-name strong{font-size:10px}.fmf-event-reading{grid-column:1/4}.fmf-event-row>.fmf-event-value:nth-of-type(4){grid-column:1/2}.fmf-event-row>.fmf-event-value:nth-of-type(5){grid-column:2/4}.fmf-event-actual strong{min-height:22px;padding:3px 7px}}

        @media(max-width:520px){.fmf-backdrop{padding:0}.fmf-modal,.fmf-modal.is-event,.fmf-modal.is-large{width:100%;max-height:100vh;height:100vh;border-radius:0}.fmf-modal-header{padding:16px}.fmf-modal-body{padding:15px}.fmf-modal-eyebrow{font-size:7px}.fmf-event-kpis{grid-template-columns:1fr 1fr}.fmf-event-kpis>div:last-child{grid-column:1/-1}.fmf-compare-toolbar{grid-template-columns:1fr}.fmf-swap-button{margin:auto;transform:rotate(90deg)}.fmf-swap-button:hover{transform:rotate(270deg)}.fmf-compare-stage{grid-template-columns:1fr}.fmf-forex-readout{grid-template-columns:36px minmax(0,1fr)}.fmf-forex-readout>b{grid-column:2}.fmf-event-hero-flag{width:46px;height:46px;flex-basis:46px}.fmf-trader-reading{grid-template-columns:40px minmax(0,1fr)}.fmf-trader-reading-icon{width:40px;height:40px}}


        /* ===== Refonte lisibilité + espace d'outils ===== */
        .fmf-shell{max-width:1680px}
        .fmf-card{border-color:color-mix(in srgb,var(--border) 86%,transparent);box-shadow:0 18px 48px -38px rgba(15,23,42,.42)}
        .fmf-section-title h2{font-size:19px;letter-spacing:-.025em}
        .fmf-section-title p{font-size:12px;line-height:1.45}
        .fmf-kicker{font-size:9px}
        .fmf-summary-foot{font-size:10px}
        .fmf-quick-item span{font-size:9px}
        .fmf-quick-item strong{font-size:11px}
        .fmf-insight-copy>p{font-size:11px}
        .fmf-reasons div{font-size:9px}
        .fmf-currency strong,.fmf-market h3{font-size:10px}

        .fmf-currency-mark{width:34px;height:34px;display:inline-grid;place-items:center;flex:0 0 34px;border:1px solid rgba(118,55,238,.14);border-radius:11px;background:linear-gradient(145deg,var(--psoft),var(--card));color:var(--purple);font-size:10px;font-weight:900;letter-spacing:.04em;line-height:1;box-shadow:0 8px 18px -15px rgba(118,55,238,.72)}
        .fmf-event-flag.fmf-currency-mark{width:36px;height:36px;flex-basis:36px;border-radius:11px;font-size:10px}
        .fmf-event-hero-flag.fmf-currency-mark{width:56px;height:56px;flex-basis:56px;border-radius:17px;font-size:14px}
        .fmf-ranking-mark.fmf-currency-mark{width:25px;height:25px;flex-basis:25px;border-radius:8px;font-size:7px}

        .fmf-tools{display:grid;grid-template-columns:minmax(0,1.3fr) minmax(410px,.7fr);align-items:start;gap:16px;margin:0 0 16px}
        .fmf-tools-stack{display:grid;grid-template-columns:minmax(0,1fr);gap:16px;min-width:0}
        .fmf-tool-card{min-width:0;padding:20px;border-radius:22px}
        .fmf-tools .fmf-section-head{margin-bottom:18px}
        .fmf-tools .fmf-section-title{align-items:flex-start}
        .fmf-tools .fmf-section-title h2{font-size:20px}
        .fmf-tools .fmf-section-title p{max-width:470px;margin-top:5px;font-size:11px;line-height:1.5}
        .fmf-comparator-card{min-height:100%;padding:22px;background:
          radial-gradient(circle at 100% 0%,rgba(118,55,238,.09),transparent 34%),
          linear-gradient(165deg,var(--card),var(--soft));border-color:rgba(118,55,238,.2)}
        .fmf-premium-label{padding:7px 10px;font-size:9px}
        .fmf-compare-toolbar{grid-template-columns:minmax(0,1fr) 48px minmax(0,1fr);gap:12px}
        .fmf-compare-toolbar label>span{margin-bottom:7px;font-size:9px;letter-spacing:.055em}
        .fmf-compare-toolbar label>div{min-height:54px;grid-template-columns:36px minmax(0,1fr) 18px;gap:9px;padding:0 13px;border-radius:15px;background:var(--card)}
        .fmf-compare-toolbar .fmf-currency-mark{width:34px;height:34px}
        .fmf-compare-toolbar select{font-size:14px}
        .fmf-swap-button{width:48px;height:48px;border-radius:15px;font-size:17px}
        .fmf-pair-rule,.fmf-score-help{gap:10px;margin:13px 0;padding:12px 13px;border-radius:13px;font-size:10px;line-height:1.55}
        .fmf-compare-stage{grid-template-columns:minmax(0,1fr) minmax(176px,.78fr) minmax(0,1fr);gap:12px;margin-top:14px}
        .fmf-compare-currency{grid-template-columns:48px minmax(0,1fr) auto;gap:12px;min-height:150px;padding:17px 16px 31px;border-radius:18px}
        .fmf-compare-flag.fmf-currency-mark{width:48px;height:48px;border-radius:14px;font-size:12px}
        .fmf-compare-currency div span{font-size:8px}
        .fmf-compare-currency div strong{font-size:18px}
        .fmf-compare-currency div small{font-size:10px}
        .fmf-compare-currency>b{font-size:25px}
        .fmf-compare-currency em{margin-top:8px;font-size:9px;line-height:1.35}
        .fmf-score-track{inset:auto 16px 13px;height:6px}
        .fmf-compare-center{min-height:150px;padding:15px 12px;border-radius:18px}
        .fmf-edge-badge{padding:5px 9px;font-size:8px}
        .fmf-compare-center>strong{margin:9px 0 6px;font-size:18px}
        .fmf-balance-meter{height:8px;margin-top:11px}
        .fmf-gap-value{margin-top:9px;font-size:18px}
        .fmf-compare-center>small{font-size:9px;line-height:1.35}
        .fmf-forex-readout{grid-template-columns:44px minmax(0,1fr) auto;gap:12px;margin-top:14px;padding:14px 15px;border-radius:16px}
        .fmf-forex-readout>span{width:44px;height:44px;border-radius:13px;font-size:20px}
        .fmf-forex-readout small{font-size:8px}
        .fmf-forex-readout strong{margin-top:3px;font-size:12px;line-height:1.45}
        .fmf-forex-readout p{margin-top:5px;font-size:10px;line-height:1.45}
        .fmf-forex-readout>b{font-size:20px}

        .fmf-impact-card,.fmf-risk-card{background:linear-gradient(160deg,var(--card),var(--soft))}
        .fmf-tool-select{display:grid;grid-template-columns:minmax(120px,.65fr) minmax(0,1.35fr);align-items:center;gap:12px;margin-bottom:12px;padding:11px 12px;border:1px solid var(--border);border-radius:15px;background:var(--card)}
        .fmf-tool-select>span small,.fmf-tool-select>span strong{display:block}
        .fmf-tool-select>span small{color:var(--muted);font-size:8px;font-weight:850;letter-spacing:.055em;text-transform:uppercase}
        .fmf-tool-select>span strong{margin-top:3px;font-size:11px}
        .fmf-tool-select>div{position:relative;display:flex;align-items:center;min-width:0;min-height:42px;padding:0 34px 0 11px;border:1px solid var(--line);border-radius:12px;background:var(--soft)}
        .fmf-tool-select select{width:100%;min-width:0;appearance:none;border:0;outline:0;background:transparent;color:var(--text);font-size:11px;font-weight:750;text-overflow:ellipsis}
        .fmf-tool-select svg{position:absolute;right:11px;color:var(--muted)}
        .fmf-impact-list{display:flex;flex-direction:column;gap:8px}
        .fmf-impact-list>div{position:relative;display:grid;grid-template-columns:minmax(0,1fr) 112px;align-items:center;gap:12px;min-height:67px;padding:11px 12px 11px 15px;border:1px solid var(--line);border-radius:14px;background:var(--card);overflow:hidden}
        .fmf-impact-list>div::before{content:"";position:absolute;inset:10px auto 10px 0;width:3px;border-radius:0 999px 999px 0;background:var(--muted)}
        .fmf-impact-list>div.is-up::before{background:var(--green)}
        .fmf-impact-list>div.is-down::before{background:var(--red)}
        .fmf-impact-symbol{min-width:0}
        .fmf-impact-symbol strong,.fmf-impact-symbol small{display:block}
        .fmf-impact-symbol strong{font-size:13px}
        .fmf-impact-symbol small{margin-top:4px;overflow:hidden;color:var(--muted);font-size:10px;line-height:1.4;text-overflow:ellipsis;white-space:nowrap}
        .fmf-impact-signal{display:grid;grid-template-columns:1fr auto;align-items:center;gap:5px}
        .fmf-impact-signal>.fmf-direction{grid-column:1/-1;justify-self:end}
        .fmf-impact-confidence{height:6px;overflow:hidden;border-radius:999px;background:var(--line)}
        .fmf-impact-confidence i{display:block;height:100%;border-radius:inherit;background:linear-gradient(90deg,var(--purple),var(--purple2))}
        .fmf-impact-signal>small{color:var(--muted);font-size:9px;font-weight:800}
        .fmf-tool-cta{width:100%;display:flex;align-items:center;justify-content:space-between;gap:12px;margin-top:12px;padding:12px 13px;border:1px solid rgba(118,55,238,.16);border-radius:14px;background:var(--psoft);color:var(--purple)!important;text-align:left;cursor:pointer;transition:.16s ease}
        .fmf-tool-cta:hover{border-color:rgba(118,55,238,.35);transform:translateY(-1px)}
        .fmf-tool-cta small,.fmf-tool-cta strong{display:block}
        .fmf-tool-cta small{font-size:8px;text-transform:uppercase;letter-spacing:.055em}
        .fmf-tool-cta strong{margin-top:3px;color:var(--text);font-size:10px}

        .fmf-risk-count{display:inline-flex;padding:6px 9px;border-radius:999px;background:var(--soft);color:var(--sub);font-size:8px;font-weight:850;white-space:nowrap}
        .fmf-risk-count.is-danger{color:var(--red);background:var(--rsoft)}
        .fmf-risk-list{display:flex;flex-direction:column;gap:7px}
        .fmf-risk-list button{display:grid;grid-template-columns:38px 57px minmax(0,1fr) auto 17px;align-items:center;gap:10px;min-height:62px;padding:9px 10px;border:1px solid var(--line);border-radius:14px;background:var(--card);text-align:left;cursor:pointer;transition:.15s ease}
        .fmf-risk-list button:hover{border-color:rgba(118,55,238,.23);background:var(--psoft);transform:translateX(2px)}
        .fmf-risk-index{width:34px;height:34px;display:grid;place-items:center;border-radius:10px;background:var(--gsoft);color:var(--green);font-size:9px;font-weight:900}
        .fmf-risk-index.is-fort{color:var(--red);background:var(--rsoft)}
        .fmf-risk-index.is-moyen{color:var(--amber);background:var(--asoft)}
        .fmf-risk-time time,.fmf-risk-time small,.fmf-risk-copy strong,.fmf-risk-copy small{display:block}
        .fmf-risk-time time{font-size:12px;font-weight:850}
        .fmf-risk-time small{margin-top:2px;color:var(--muted);font-size:8px}
        .fmf-risk-copy{min-width:0}
        .fmf-risk-copy strong{overflow:hidden;font-size:11px;text-overflow:ellipsis;white-space:nowrap}
        .fmf-risk-copy small{margin-top:3px;color:var(--muted);font-size:9px}
        .fmf-risk-list button>.fmf-pill{min-width:52px}
        .fmf-risk-guidance{display:flex;align-items:flex-start;gap:9px;margin-top:11px;padding:11px 12px;border:1px solid rgba(217,130,8,.16);border-radius:13px;background:var(--asoft);color:var(--amber)}
        .fmf-risk-guidance span{color:var(--sub);font-size:9px;line-height:1.45}
        .fmf-risk-guidance strong{display:block;margin-bottom:2px;color:var(--amber);font-size:9px}

        @media(max-width:1320px){
          .fmf-tools{grid-template-columns:1fr}
          .fmf-tools-stack{grid-template-columns:repeat(2,minmax(0,1fr))}
        }
        @media(max-width:980px){
          .fmf-tools-stack{grid-template-columns:1fr}
          .fmf-compare-stage{grid-template-columns:minmax(0,1fr) 170px minmax(0,1fr)}
        }
        @media(max-width:800px){
          .fmf-compare-stage{grid-template-columns:1fr}
          .fmf-compare-center{order:-1;min-height:auto}
          .fmf-compare-currency{min-height:132px}
        }
        @media(max-width:680px){
          .fmf-tool-card,.fmf-comparator-card{padding:15px}
          .fmf-tools .fmf-section-head{align-items:flex-start}
          .fmf-tool-select{grid-template-columns:1fr}
          .fmf-risk-list button{grid-template-columns:38px 55px minmax(0,1fr) 16px}
          .fmf-risk-list button>.fmf-pill{grid-column:3;justify-self:start}
          .fmf-risk-list button>svg{grid-column:4;grid-row:1/3}
        }
        @media(max-width:560px){
          .fmf-compare-toolbar{grid-template-columns:1fr}
          .fmf-swap-button{margin:auto;transform:rotate(90deg)}
          .fmf-swap-button:hover{transform:rotate(270deg)}
          .fmf-compare-currency{grid-template-columns:44px minmax(0,1fr) auto}
          .fmf-compare-flag.fmf-currency-mark{width:44px;height:44px}
          .fmf-forex-readout{grid-template-columns:40px minmax(0,1fr)}
          .fmf-forex-readout>b{grid-column:2}
          .fmf-impact-list>div{grid-template-columns:1fr}
          .fmf-impact-signal{grid-template-columns:minmax(0,1fr) auto}
          .fmf-impact-signal>.fmf-direction{grid-column:auto;grid-row:1;justify-self:start}
          .fmf-risk-list button{grid-template-columns:36px minmax(0,1fr) 16px;align-items:start}
          .fmf-risk-time{grid-column:2}
          .fmf-risk-copy{grid-column:2}
          .fmf-risk-list button>.fmf-pill{grid-column:2}
          .fmf-risk-list button>svg{grid-column:3;grid-row:1/4;align-self:center}
        }


        /* ===== Sélecteur de devises premium + moteur multi-facteurs ===== */
        .fmf-comparator-card{overflow:visible;position:relative;z-index:4}
        .fmf-compare-toolbar{position:relative;z-index:12;align-items:end}
        .fmf-currency-select{position:relative;min-width:0}
        .fmf-currency-select-label{display:block;margin-bottom:8px;color:var(--muted);font-size:9px;font-weight:850;letter-spacing:.055em;text-transform:uppercase}
        .fmf-currency-select-trigger{width:100%;min-height:64px;display:grid;grid-template-columns:38px minmax(0,1fr) auto 18px;align-items:center;gap:11px;padding:9px 12px;border:1px solid var(--border);border-radius:17px;background:var(--card);text-align:left;cursor:pointer;transition:border-color .16s ease,box-shadow .16s ease,transform .16s ease}
        .fmf-currency-select-trigger:hover{border-color:rgba(118,55,238,.32);transform:translateY(-1px)}
        .fmf-currency-select.is-open .fmf-currency-select-trigger{border-color:rgba(118,55,238,.58);box-shadow:0 0 0 4px rgba(118,55,238,.09),0 18px 38px -28px rgba(118,55,238,.65)}
        .fmf-currency-select-copy,.fmf-currency-select-score{min-width:0}
        .fmf-currency-select-copy strong,.fmf-currency-select-copy small,.fmf-currency-select-score b,.fmf-currency-select-score small{display:block}
        .fmf-currency-select-copy strong{font-size:15px;line-height:1.15}
        .fmf-currency-select-copy small{margin-top:4px;overflow:hidden;color:var(--muted);font-size:9px;text-overflow:ellipsis;white-space:nowrap}
        .fmf-currency-select-score{text-align:right}
        .fmf-currency-select-score b{font-size:16px;line-height:1}
        .fmf-currency-select-score small{margin-top:3px;color:var(--muted);font-size:7px}
        .fmf-currency-select-chevron{color:var(--muted);transition:transform .16s ease}
        .fmf-currency-select.is-open .fmf-currency-select-chevron{transform:rotate(180deg)}
        .fmf-currency-select-menu{position:absolute;top:calc(100% + 9px);left:0;right:0;z-index:80;overflow:hidden;border:1px solid rgba(118,55,238,.22);border-radius:18px;background:var(--card);box-shadow:0 28px 70px rgba(15,23,42,.22),0 0 0 1px rgba(255,255,255,.35) inset;animation:fmfSelectIn .16s ease-out}
        @keyframes fmfSelectIn{from{opacity:0;transform:translateY(-5px) scale(.99)}to{opacity:1;transform:none}}
        .fmf-currency-select-search{display:flex;align-items:center;gap:8px;margin:10px;padding:0 11px;min-height:42px;border:1px solid var(--line);border-radius:12px;background:var(--soft);color:var(--muted)}
        .fmf-currency-select-search input{width:100%;border:0;outline:0;background:transparent;color:var(--text);font-size:10px}
        .fmf-currency-select-options{max-height:330px;overflow-y:auto;padding:0 8px 8px;scrollbar-width:thin;scrollbar-color:rgba(118,55,238,.35) transparent}
        .fmf-currency-select-options>button{width:100%;display:grid;grid-template-columns:36px minmax(0,1fr) auto;align-items:center;gap:10px;padding:9px 10px;border:1px solid transparent;border-radius:12px;background:transparent;text-align:left;cursor:pointer;transition:.14s ease}
        .fmf-currency-select-options>button:hover{border-color:var(--line);background:var(--soft)}
        .fmf-currency-select-options>button.is-active{border-color:rgba(118,55,238,.18);background:var(--psoft)}
        .fmf-currency-select-options>button.is-disabled{opacity:.38;cursor:not-allowed}
        .fmf-currency-select-options>button>span:nth-child(2){min-width:0}
        .fmf-currency-select-options strong,.fmf-currency-select-options small{display:block}
        .fmf-currency-select-options strong{font-size:11px}
        .fmf-currency-select-options small{margin-top:3px;overflow:hidden;color:var(--muted);font-size:8px;text-overflow:ellipsis;white-space:nowrap}
        .fmf-currency-option-meta{display:flex!important;align-items:flex-end;flex-direction:column;gap:4px;text-align:right}
        .fmf-currency-option-meta>b{font-size:12px}
        .fmf-currency-select-empty{padding:18px;color:var(--muted);font-size:10px;text-align:center}

        .fmf-score-quality{display:inline-flex!important;align-items:center;gap:5px;width:max-content;margin-top:5px;padding:4px 7px;border-radius:999px;background:var(--soft);color:var(--muted);font-size:7px!important;font-weight:800;line-height:1!important}
        .fmf-score-quality i{width:6px;height:6px;border-radius:50%;background:currentColor}
        .fmf-score-quality.is-confirmed{color:var(--green);background:var(--gsoft)}
        .fmf-score-quality.is-limited,.fmf-score-quality.is-provisional{color:var(--amber);background:var(--asoft)}
        .fmf-score-quality.is-insufficient{color:var(--muted);background:var(--soft)}
        .fmf-score-quality.is-compact{padding:3px 6px;font-size:6px!important}
        .fmf-score-help-v2{display:grid;grid-template-columns:auto minmax(0,1fr) auto;align-items:start}
        .fmf-score-help-meta{display:grid;grid-template-columns:auto auto;gap:3px 7px;padding-left:12px;border-left:1px solid rgba(118,55,238,.13);white-space:nowrap}
        .fmf-score-help-meta b{color:var(--purple);font-size:10px;text-align:right}

        .fmf-engine-strip{display:grid;grid-template-columns:minmax(320px,1.1fr) minmax(430px,1fr);gap:14px;margin-bottom:14px;padding:15px 17px;border-color:rgba(118,55,238,.16);background:linear-gradient(135deg,var(--card),var(--psoft))}
        .fmf-engine-intro{display:flex;align-items:center;gap:12px;min-width:0}
        .fmf-engine-icon{width:44px;height:44px;display:grid;place-items:center;flex:0 0 44px;border-radius:14px;color:#fff;background:linear-gradient(145deg,var(--purple),var(--purple2));font-size:20px;box-shadow:0 12px 25px rgba(118,55,238,.22)}
        .fmf-engine-intro small,.fmf-engine-intro strong,.fmf-engine-intro p{display:block}
        .fmf-engine-intro small{color:var(--purple);font-size:8px;font-weight:900;letter-spacing:.08em;text-transform:uppercase}
        .fmf-engine-intro strong{margin-top:3px;font-size:13px}
        .fmf-engine-intro p{margin:4px 0 0;color:var(--sub);font-size:9px;line-height:1.45}
        .fmf-engine-metrics{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:8px}
        .fmf-engine-metrics>div{display:flex;align-items:center;gap:8px;padding:9px 10px;border:1px solid var(--line);border-radius:12px;background:var(--card)}
        .fmf-engine-metrics>div>svg{flex:0 0 auto;color:var(--purple);font-size:16px}
        .fmf-engine-metrics small,.fmf-engine-metrics strong{display:block}
        .fmf-engine-metrics small{color:var(--muted);font-size:7px}
        .fmf-engine-metrics strong{margin-top:2px;font-size:9px}
        .fmf-engine-factors{grid-column:1/-1;display:flex;flex-wrap:wrap;gap:6px;padding-top:2px}
        .fmf-engine-factors span{display:inline-flex;align-items:center;gap:5px;padding:5px 8px;border:1px solid var(--line);border-radius:999px;background:var(--card);color:var(--sub);font-size:7px}
        .fmf-engine-factors svg{color:var(--green)}

        .fmf-score-composition{display:grid;grid-template-columns:repeat(4,minmax(0,1fr));gap:8px;margin-top:12px}
        .fmf-score-composition span{padding:10px;border:1px solid var(--line);border-radius:11px;background:var(--soft)}
        .fmf-score-composition small,.fmf-score-composition strong{display:block}
        .fmf-score-composition small{color:var(--muted);font-size:7px;text-transform:uppercase}
        .fmf-score-composition strong{margin-top:5px;font-size:12px}
        .fmf-modal-grid{grid-template-columns:repeat(4,minmax(0,1fr))}

        @media(max-width:1180px){
          .fmf-engine-strip{grid-template-columns:1fr}
          .fmf-engine-factors{grid-column:auto}
        }
        @media(max-width:700px){
          .fmf-engine-metrics{grid-template-columns:1fr}
          .fmf-score-help-v2{grid-template-columns:auto minmax(0,1fr)}
          .fmf-score-help-meta{grid-column:2;margin-top:8px;padding:8px 0 0;border-left:0;border-top:1px solid rgba(118,55,238,.13)}
          .fmf-score-composition,.fmf-modal-grid{grid-template-columns:1fr 1fr}
        }
        @media(max-width:560px){
          .fmf-currency-select-trigger{min-height:60px}
          .fmf-currency-select-menu{position:fixed;left:10px;right:10px;top:auto;bottom:10px;max-height:72vh;border-radius:20px}
          .fmf-currency-select-options{max-height:55vh}
          .fmf-engine-strip{padding:14px}
          .fmf-engine-intro{align-items:flex-start}
        }


        /* ===== Event selector premium + explications pédagogiques ===== */
        .fmf-impact-card{overflow:visible;position:relative;z-index:3}
        .fmf-event-select{position:relative;z-index:30;margin-bottom:14px}
        .fmf-event-select-trigger{width:100%;min-height:76px;display:grid;grid-template-columns:42px minmax(0,1fr) auto 18px;align-items:center;gap:12px;padding:11px 13px;border:1px solid var(--border);border-radius:17px;background:var(--card);text-align:left;cursor:pointer;transition:.16s ease}
        .fmf-event-select-trigger:hover{border-color:rgba(118,55,238,.3);transform:translateY(-1px)}
        .fmf-event-select.is-open .fmf-event-select-trigger{border-color:rgba(118,55,238,.55);box-shadow:0 0 0 4px rgba(118,55,238,.08),0 18px 38px -30px rgba(118,55,238,.7)}
        .fmf-event-select-mark.fmf-currency-mark{width:42px;height:42px;flex-basis:42px;border-radius:13px;font-size:10px}
        .fmf-event-select-current{min-width:0}.fmf-event-select-current small,.fmf-event-select-current strong,.fmf-event-select-current em{display:block}
        .fmf-event-select-current small{color:var(--purple);font-size:8px;font-weight:850;letter-spacing:.04em;text-transform:uppercase}
        .fmf-event-select-current strong{margin-top:3px;overflow:hidden;font-size:12px;line-height:1.3;text-overflow:ellipsis;white-space:nowrap}
        .fmf-event-select-current em{margin-top:4px;overflow:hidden;color:var(--muted);font-size:9px;font-style:normal;text-overflow:ellipsis;white-space:nowrap}
        .fmf-event-select-chevron{color:var(--muted);transition:transform .16s ease}.fmf-event-select.is-open .fmf-event-select-chevron{transform:rotate(180deg)}
        .fmf-event-select-menu{position:absolute;top:calc(100% + 9px);left:0;right:0;z-index:120;overflow:hidden;border:1px solid rgba(118,55,238,.2);border-radius:19px;background:var(--card);box-shadow:0 30px 76px rgba(15,23,42,.24);animation:fmfSelectIn .16s ease-out}
        .fmf-event-select-search{display:grid;grid-template-columns:18px minmax(0,1fr) auto;align-items:center;gap:8px;margin:10px;padding:0 11px;min-height:44px;border:1px solid var(--line);border-radius:13px;background:var(--soft);color:var(--muted)}
        .fmf-event-select-search input{width:100%;border:0;outline:0;background:transparent;color:var(--text);font-size:10px}.fmf-event-select-search>span{font-size:8px;font-weight:850}
        .fmf-event-select-options{max-height:390px;overflow-y:auto;padding:0 8px 8px;scrollbar-width:thin;scrollbar-color:rgba(118,55,238,.35) transparent}
        .fmf-event-select-options>button{width:100%;display:grid;grid-template-columns:52px 36px minmax(0,1fr) auto;align-items:center;gap:10px;padding:10px;border:1px solid transparent;border-radius:14px;background:transparent;text-align:left;cursor:pointer;transition:.14s ease}
        .fmf-event-select-options>button:hover{border-color:var(--line);background:var(--soft)}.fmf-event-select-options>button.is-active{border-color:rgba(118,55,238,.2);background:var(--psoft)}
        .fmf-event-option-time strong,.fmf-event-option-time small,.fmf-event-option-copy strong,.fmf-event-option-copy small,.fmf-event-option-copy em,.fmf-event-option-status small{display:block}
        .fmf-event-option-time strong{font-size:11px}.fmf-event-option-time small{margin-top:3px;color:var(--muted);font-size:7px}
        .fmf-event-option-mark.fmf-currency-mark{width:34px;height:34px;flex-basis:34px;font-size:8px}
        .fmf-event-option-copy{min-width:0}.fmf-event-option-copy strong{overflow:hidden;font-size:10px;text-overflow:ellipsis;white-space:nowrap}.fmf-event-option-copy small{margin-top:3px;color:var(--muted);font-size:8px}.fmf-event-option-copy em{margin-top:4px;overflow:hidden;color:var(--sub);font-size:8px;font-style:normal;text-overflow:ellipsis;white-space:nowrap}
        .fmf-event-option-status{text-align:right}.fmf-event-option-status small{margin-top:5px;color:var(--muted);font-size:7px}.fmf-event-select-empty{padding:24px;color:var(--muted);font-size:10px;text-align:center}

        .fmf-impact-explainer{margin-bottom:12px;padding:14px;border:1px solid rgba(118,55,238,.16);border-radius:16px;background:linear-gradient(135deg,var(--psoft),var(--card))}
        .fmf-impact-explainer>header{display:flex;align-items:center;gap:10px}.fmf-impact-explainer>header>span{width:36px;height:36px;display:grid;place-items:center;flex:0 0 36px;border-radius:11px;color:#fff;background:linear-gradient(145deg,var(--purple),var(--purple2))}
        .fmf-impact-explainer header small,.fmf-impact-explainer header strong{display:block}.fmf-impact-explainer header small{color:var(--purple);font-size:8px;font-weight:850;text-transform:uppercase}.fmf-impact-explainer header strong{margin-top:2px;font-size:11px}.fmf-impact-explainer>p{margin:9px 0 0;color:var(--sub);font-size:9px;line-height:1.5}
        .fmf-impact-data-grid{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:7px;margin-top:11px}.fmf-impact-data-grid>div{padding:9px;border:1px solid var(--line);border-radius:11px;background:var(--card)}
        .fmf-impact-data-grid small,.fmf-impact-data-grid strong,.fmf-impact-data-grid span{display:block}.fmf-impact-data-grid small{color:var(--muted);font-size:7px;text-transform:uppercase}.fmf-impact-data-grid strong{margin-top:4px;font-size:13px}.fmf-impact-data-grid span{margin-top:3px;color:var(--muted);font-size:7px;line-height:1.35}
        .fmf-impact-data-grid>div.is-up{border-color:rgba(7,151,106,.2);background:var(--gsoft)}.fmf-impact-data-grid>div.is-down{border-color:rgba(223,56,89,.2);background:var(--rsoft)}
        .fmf-impact-comparison{display:flex;align-items:flex-start;gap:8px;margin-top:9px;padding:9px 10px;border-radius:11px;background:var(--soft);color:var(--purple)}.fmf-impact-comparison span{color:var(--sub)}.fmf-impact-comparison small,.fmf-impact-comparison strong{display:block}.fmf-impact-comparison small{font-size:7px;text-transform:uppercase}.fmf-impact-comparison strong{margin-top:2px;font-size:9px;line-height:1.45}

        .fmf-data-story{padding:16px;border:1px solid rgba(118,55,238,.16);border-radius:17px;background:linear-gradient(145deg,var(--card),var(--psoft))}.fmf-data-story>header{display:flex;align-items:center;gap:10px}.fmf-data-story>header>span{width:40px;height:40px;display:grid;place-items:center;border-radius:12px;color:var(--purple);background:var(--psoft);font-size:18px}.fmf-data-story header small,.fmf-data-story header strong{display:block}.fmf-data-story header small{color:var(--purple);font-size:8px;font-weight:850;text-transform:uppercase}.fmf-data-story header strong{margin-top:2px;font-size:12px}
        .fmf-data-story-grid{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:9px;margin-top:12px}.fmf-data-story-grid article{padding:12px;border:1px solid var(--line);border-radius:13px;background:var(--card)}.fmf-data-story-grid small,.fmf-data-story-grid strong{display:block}.fmf-data-story-grid small{color:var(--muted);font-size:8px;text-transform:uppercase}.fmf-data-story-grid strong{margin-top:5px;font-size:16px}.fmf-data-story-grid p{margin:5px 0 0;color:var(--sub);font-size:9px;line-height:1.45}
        .fmf-data-story-reading{display:flex;align-items:flex-start;gap:10px;margin-top:10px;padding:12px;border:1px solid var(--line);border-radius:13px;background:var(--soft);color:var(--purple)}.fmf-data-story-reading span{color:var(--sub)}.fmf-data-story-reading small,.fmf-data-story-reading strong{display:block}.fmf-data-story-reading small{font-size:8px;text-transform:uppercase}.fmf-data-story-reading strong{margin-top:3px;font-size:11px}.fmf-data-story-reading p{margin:4px 0 0;font-size:9px;line-height:1.45}
        .fmf-analysis-steps{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:7px;margin-top:10px}.fmf-analysis-steps span{display:flex;align-items:flex-start;gap:7px;padding:9px;border:1px solid var(--line);border-radius:11px;background:var(--card);color:var(--sub);font-size:8px;line-height:1.4}.fmf-analysis-steps b{width:20px;height:20px;display:grid;place-items:center;flex:0 0 20px;border-radius:7px;color:#fff;background:var(--purple);font-size:7px}

        .fmf-explanation-panel{margin-top:12px;padding:14px;border:1px solid rgba(118,55,238,.16);border-radius:15px;background:linear-gradient(135deg,var(--psoft),var(--card))}.fmf-explanation-panel>header{display:flex;align-items:flex-start;gap:9px;color:var(--purple)}.fmf-explanation-panel header small,.fmf-explanation-panel header strong{display:block}.fmf-explanation-panel header small{font-size:8px;text-transform:uppercase}.fmf-explanation-panel header strong{margin-top:2px;color:var(--text);font-size:11px}.fmf-explanation-panel>p{margin:9px 0 0;color:var(--sub);font-size:9px;line-height:1.5}.fmf-explanation-columns{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:7px;margin-top:10px}.fmf-explanation-columns>div{padding:9px;border:1px solid var(--line);border-radius:11px;background:var(--card)}.fmf-explanation-columns small,.fmf-explanation-columns strong{display:block}.fmf-explanation-columns small{color:var(--muted);font-size:7px;text-transform:uppercase}.fmf-explanation-columns strong{margin-top:3px;font-size:8px;line-height:1.45}
        .fmf-driver-table{display:flex;flex-direction:column;gap:6px}.fmf-driver-table>div{display:grid;grid-template-columns:minmax(0,1fr) auto;align-items:center;gap:10px;padding:10px 11px;border:1px solid var(--line);border-radius:12px;background:var(--soft)}.fmf-driver-table span small,.fmf-driver-table span strong,.fmf-driver-table span em{display:block}.fmf-driver-table span small{color:var(--muted);font-size:7px;text-transform:uppercase}.fmf-driver-table span strong{margin-top:3px;font-size:9px}.fmf-driver-table span em{margin-top:3px;color:var(--sub);font-size:8px;font-style:normal}.fmf-driver-table>div>b{font-size:11px}.fmf-driver-table>div.is-positive>b{color:var(--green)}.fmf-driver-table>div.is-negative>b{color:var(--red)}
        .fmf-pair-explanation{display:grid;grid-template-columns:1fr 34px 1fr;align-items:center;gap:8px;margin-top:12px}.fmf-pair-explanation>div{padding:12px;border:1px solid var(--line);border-radius:13px;background:var(--soft)}.fmf-pair-explanation>span{width:34px;height:34px;display:grid;place-items:center;border-radius:10px;color:var(--purple);background:var(--psoft)}.fmf-pair-explanation small,.fmf-pair-explanation strong{display:block}.fmf-pair-explanation small{color:var(--muted);font-size:7px;text-transform:uppercase}.fmf-pair-explanation strong{margin-top:4px;font-size:10px}.fmf-pair-explanation p{margin:4px 0 0;color:var(--sub);font-size:8px;line-height:1.4}

        @media(max-width:680px){.fmf-event-select-menu{position:fixed;left:8px;right:8px;top:auto;bottom:8px;max-height:78vh}.fmf-event-select-options{max-height:62vh}.fmf-event-select-options>button{grid-template-columns:48px 34px minmax(0,1fr)}.fmf-event-option-status{grid-column:3;text-align:left}.fmf-impact-data-grid,.fmf-data-story-grid,.fmf-analysis-steps,.fmf-explanation-columns{grid-template-columns:1fr}.fmf-pair-explanation{grid-template-columns:1fr}.fmf-pair-explanation>span{margin:auto;transform:rotate(90deg)}}
        @media(max-width:520px){.fmf-event-select-trigger{grid-template-columns:38px minmax(0,1fr) 18px}.fmf-event-select-trigger>.fmf-pill{grid-column:2;justify-self:start}.fmf-event-select-chevron{grid-column:3;grid-row:1/3}.fmf-event-select-current strong{white-space:normal}.fmf-impact-explainer{padding:12px}}


        /* ===== Skeleton de chargement — même logique que les changements d’onglet ===== */
        .fmf-dashboard-skeleton{display:flex;flex-direction:column;gap:12px;width:100%}
        .fmf-skeleton-heading{display:flex;align-items:center;gap:12px;padding:12px 14px;border:1px solid var(--border);border-radius:16px;background:var(--card);color:var(--sub)}
        .fmf-skeleton-heading-icon{width:38px;height:38px;display:grid;place-items:center;flex:0 0 38px;border-radius:12px;background:var(--psoft);color:var(--purple);font-size:18px}
        .fmf-skeleton-heading strong,.fmf-skeleton-heading span{display:block}.fmf-skeleton-heading strong{color:var(--text);font-size:11px}.fmf-skeleton-heading span{margin-top:2px;color:var(--muted);font-size:9px}
        .fmf-skeleton-summary{display:grid;grid-template-columns:repeat(4,minmax(0,1fr));gap:11px}
        .fmf-skeleton-card{position:relative;overflow:hidden;border:1px solid var(--border);border-radius:18px;background:var(--card)}
        .fmf-skeleton-card::after{content:"";position:absolute;inset:0;transform:translateX(-115%);background:linear-gradient(90deg,transparent,rgba(255,255,255,.72),transparent);animation:fmfSkeletonSweep 1.35s ease-in-out infinite;pointer-events:none}
        html.dark .fmf-skeleton-card::after,body.dark .fmf-skeleton-card::after,.dark .fmf-skeleton-card::after,[data-theme="dark"] .fmf-skeleton-card::after{background:linear-gradient(90deg,transparent,rgba(255,255,255,.055),transparent)}
        .fmf-skeleton-block{display:block;overflow:hidden;border-radius:999px;background:linear-gradient(90deg,var(--line),color-mix(in srgb,var(--line) 68%,var(--card)),var(--line));background-size:220% 100%;animation:fmfSkeletonPulse 1.45s ease-in-out infinite}
        .fmf-skeleton-summary-card{min-height:122px;padding:14px}.fmf-skeleton-card-top,.fmf-skeleton-card-foot{display:flex;align-items:center;justify-content:space-between;gap:10px}.fmf-skeleton-card-foot{margin-top:14px}
        .fmf-skeleton-block.is-icon{width:38px;height:38px;border-radius:12px}.fmf-skeleton-block.is-icon-large{width:42px;height:42px;border-radius:13px}.fmf-skeleton-block.is-pill{width:58px;height:20px}.fmf-skeleton-block.is-label{width:44%;height:8px;margin-top:12px}.fmf-skeleton-block.is-label-wide{width:112px;height:9px}.fmf-skeleton-block.is-value{width:62%;height:19px;margin-top:8px}.fmf-skeleton-block.is-small{width:64px;height:7px}.fmf-skeleton-block.is-number{width:42px;height:10px}.fmf-skeleton-block.is-text{width:82%;height:10px;margin-top:7px}.fmf-skeleton-block.is-text-wide{width:min(360px,82%);height:12px;margin-top:8px}
        .fmf-skeleton-decision{display:grid;grid-template-columns:minmax(0,1.2fr) repeat(4,minmax(130px,.7fr));gap:9px;padding:13px}.fmf-skeleton-decision-main{display:flex;align-items:center;gap:11px;padding-right:11px;border-right:1px solid var(--line)}.fmf-skeleton-decision-main>div{min-width:0;flex:1}.fmf-skeleton-decision-item{padding:10px;border:1px solid var(--line);border-radius:12px;background:var(--soft)}
        .fmf-skeleton-main{display:grid;grid-template-columns:minmax(0,1.34fr) minmax(360px,.76fr);gap:14px}.fmf-skeleton-calendar,.fmf-skeleton-insight{min-height:420px;padding:18px}.fmf-skeleton-section-head{display:flex;align-items:center;justify-content:space-between;gap:12px;margin-bottom:16px}.fmf-skeleton-title-group{display:flex;align-items:center;gap:11px;min-width:0}.fmf-skeleton-title-group>div{min-width:0;flex:1}.fmf-skeleton-block.is-title{width:150px;max-width:75%;height:15px}.fmf-skeleton-block.is-subtitle{width:230px;max-width:92%;height:8px;margin-top:7px}.fmf-skeleton-block.is-button{width:92px;height:32px;border-radius:10px}.fmf-skeleton-block.is-toolbar{width:100%;height:38px;border-radius:12px}.fmf-skeleton-rows{display:flex;flex-direction:column;gap:1px;margin-top:10px;border:1px solid var(--border);border-radius:15px;overflow:hidden}.fmf-skeleton-row{display:grid;grid-template-columns:55px 34px minmax(0,1fr) 58px 42px;align-items:center;gap:10px;min-height:62px;padding:10px 12px;border-bottom:1px solid var(--line)}.fmf-skeleton-row:last-child{border-bottom:0}.fmf-skeleton-block.is-time{width:46px;height:12px}.fmf-skeleton-block.is-avatar{width:34px;height:34px;border-radius:10px}.fmf-skeleton-row-copy{min-width:0}.fmf-skeleton-block.is-row-title{width:68%;height:10px}.fmf-skeleton-block.is-row-subtitle{width:44%;height:7px;margin-top:7px}
        .fmf-skeleton-block.is-story-title{width:66%;height:17px;margin-top:20px}.fmf-skeleton-block.is-story-line{width:100%;height:9px;margin-top:12px}.fmf-skeleton-block.is-story-line.is-short{width:72%}.fmf-skeleton-insight-grid{display:grid;grid-template-columns:1fr 1fr;gap:8px;margin-top:18px}.fmf-skeleton-block.is-insight-item{height:48px;border-radius:11px}.fmf-skeleton-block.is-ring{width:98px;height:98px;margin:24px auto 0;border-radius:50%}
        .fmf-skeleton-tools{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:12px}.fmf-skeleton-tool{min-height:260px;padding:16px}.fmf-skeleton-block.is-tool-control{width:100%;height:52px;margin-top:18px;border-radius:13px}.fmf-skeleton-block.is-tool-row{width:100%;height:46px;margin-top:8px;border-radius:12px}
        @keyframes fmfSkeletonSweep{0%{transform:translateX(-115%)}60%,100%{transform:translateX(115%)}}
        @keyframes fmfSkeletonPulse{0%,100%{background-position:0 50%;opacity:.72}50%{background-position:100% 50%;opacity:1}}
        @media(max-width:1300px){.fmf-skeleton-decision{grid-template-columns:1fr 1fr 1fr}.fmf-skeleton-decision-main{grid-column:1/-1;padding-bottom:10px;border-right:0;border-bottom:1px solid var(--line)}.fmf-skeleton-main{grid-template-columns:1fr}.fmf-skeleton-tools{grid-template-columns:1fr 1fr}.fmf-skeleton-tools>:last-child{grid-column:1/-1}}
        @media(max-width:900px){.fmf-skeleton-summary{grid-template-columns:1fr 1fr}.fmf-skeleton-tools{grid-template-columns:1fr}.fmf-skeleton-tools>:last-child{grid-column:auto}}
        @media(max-width:620px){.fmf-skeleton-heading{align-items:flex-start}.fmf-skeleton-summary{grid-template-columns:1fr}.fmf-skeleton-decision{grid-template-columns:1fr}.fmf-skeleton-row{grid-template-columns:44px 30px minmax(0,1fr)}.fmf-skeleton-row>.is-pill,.fmf-skeleton-row>.is-number{display:none}.fmf-skeleton-calendar,.fmf-skeleton-insight{min-height:360px;padding:14px}.fmf-skeleton-insight-grid{grid-template-columns:1fr}.fmf-skeleton-section-head{align-items:flex-start}.fmf-skeleton-block.is-button{display:none}}
        .fmf-background-loading,.fmf-cache-note{display:flex;align-items:center;gap:8px;margin-bottom:10px;padding:8px 11px;border:1px solid var(--line);border-radius:11px;background:var(--card);color:var(--muted);font-size:9px}
        .fmf-background-loading-dot{width:7px;height:7px;flex:0 0 7px;border-radius:999px;background:var(--purple);box-shadow:0 0 0 4px var(--psoft);animation:fmfDataPulse 1.1s ease-in-out infinite}
        .fmf-cache-note{color:var(--sub);background:var(--soft)}.fmf-cache-note svg{color:var(--purple)}
        @keyframes fmfDataPulse{0%,100%{opacity:.45;transform:scale(.9)}50%{opacity:1;transform:scale(1.08)}}
        @media(prefers-reduced-motion:reduce){.fmf-skeleton-card::after,.fmf-skeleton-block,.fmf-background-loading-dot{animation:none}}

        /* ===== Trader readability + timezone v3 ===== */
        .fmf-page{font-size:14px;line-height:1.58}.fmf-title p,.fmf-section-title p,.fmf-insight-copy>p{font-size:12px;line-height:1.55}.fmf-action,.fmf-search-trigger,.fmf-link{font-size:12px}.fmf-kicker{font-size:10px}.fmf-summary-card>strong{font-size:20px;line-height:1.25}.fmf-summary-foot{font-size:11px}.fmf-quick-main h2{font-size:17px}.fmf-quick-main p{font-size:12px;line-height:1.45}.fmf-quick-item span{font-size:10px}.fmf-quick-item strong{font-size:12px;line-height:1.4;white-space:normal}.fmf-section-title h2{font-size:19px}.fmf-pill{min-width:58px;padding:6px 10px;font-size:10px}.fmf-direction,.fmf-direction.is-compact{padding:5px 8px;font-size:10px}.fmf-calendar-meta{font-size:11px}.fmf-event-head{font-size:10px}.fmf-event-row{min-height:78px}.fmf-event-when strong{font-size:14px}.fmf-event-when small,.fmf-event-name small{font-size:10px}.fmf-event-name strong{font-size:13px}.fmf-event-value strong{font-size:13px}.fmf-event-reading{font-size:11px;line-height:1.5}.fmf-currency strong,.fmf-market h3,.fmf-alert strong{font-size:12px}.fmf-change,.fmf-market footer span,.fmf-alert small,.fmf-alert>b{font-size:10px}.fmf-empty strong{font-size:13px}.fmf-empty span{font-size:11px}.fmf-engine-intro p,.fmf-engine-metrics small,.fmf-engine-factors span{font-size:11px}.fmf-premium-label,.fmf-score-quality{font-size:10px}
        .fmf-page button:focus-visible,.fmf-page input:focus-visible,.fmf-page select:focus-visible{outline:3px solid rgba(118,55,238,.25);outline-offset:2px}
        .fmf-timezone-action{max-width:190px}.fmf-timezone-action span{overflow:hidden;text-overflow:ellipsis;white-space:nowrap}.fmf-reading-guide{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:10px;margin-bottom:14px;padding:12px}.fmf-reading-status,.fmf-reading-step{display:grid;grid-template-columns:44px minmax(0,1fr);gap:11px;align-items:start;padding:14px;border:1px solid var(--line);border-radius:15px;background:var(--soft)}.fmf-reading-status>span,.fmf-reading-step>span{width:44px;height:44px;display:grid;place-items:center;border-radius:13px;color:var(--purple);background:var(--psoft);font-size:20px}.fmf-reading-status.is-open>span{color:var(--green);background:var(--gsoft)}.fmf-reading-status.is-caution>span{color:var(--amber);background:var(--asoft)}.fmf-reading-status.is-blocked>span{color:var(--red);background:var(--rsoft)}.fmf-reading-guide small,.fmf-reading-guide strong,.fmf-reading-guide p{display:block}.fmf-reading-guide small{color:var(--muted);font-size:10px;font-weight:850;text-transform:uppercase}.fmf-reading-guide strong{margin-top:4px;font-size:14px;line-height:1.35}.fmf-reading-guide p{margin:5px 0 0;color:var(--sub);font-size:11px;line-height:1.5}
        .fmf-modal{width:min(96vw,900px);max-height:94vh}.fmf-modal.is-large{width:min(97vw,1360px)}.fmf-modal.is-event{width:min(97vw,1120px)}.fmf-modal-header{padding:26px 30px 22px}.fmf-modal h2{font-size:27px}.fmf-modal-header p{font-size:13px;line-height:1.5}.fmf-modal-body{padding:26px 30px 32px;font-size:14px}.fmf-modal-eyebrow{font-size:10px}.fmf-stat span,.fmf-event-kpis span,.fmf-event-kpis small,.fmf-data-story-grid small,.fmf-trader-reading>div>span,.fmf-detail-section-title p,.fmf-event-asset footer span{font-size:10px}.fmf-stat strong{font-size:14px}.fmf-callout span,.fmf-market-detail p,.fmf-detail-list div,.fmf-data-story-grid p,.fmf-data-story-reading p,.fmf-event-asset p,.fmf-trader-reading small{font-size:12px;line-height:1.55}.fmf-event-kpis strong,.fmf-data-story-grid strong{font-size:19px}.fmf-trader-reading strong{font-size:15px}.fmf-event-hero-main strong{font-size:17px}.fmf-event-hero-main small,.fmf-publication-status{font-size:11px}.fmf-detail-section-title h3{font-size:18px}
        .fmf-timezone-settings{display:flex;flex-direction:column;gap:16px}.fmf-timezone-preview{display:grid;grid-template-columns:52px minmax(0,1fr);gap:14px;padding:17px;border:1px solid rgba(118,55,238,.18);border-radius:17px;background:linear-gradient(135deg,var(--psoft),var(--card))}.fmf-timezone-preview>span{width:52px;height:52px;display:grid;place-items:center;border-radius:15px;color:#fff;background:linear-gradient(145deg,var(--purple),var(--purple2));font-size:24px}.fmf-timezone-preview small,.fmf-timezone-preview strong{display:block}.fmf-timezone-preview small{color:var(--purple);font-size:11px;font-weight:850;text-transform:uppercase}.fmf-timezone-preview strong{margin-top:4px;font-size:17px}.fmf-timezone-preview p{margin:6px 0 0;color:var(--sub);font-size:12px}.fmf-timezone-field>span{display:block;margin-bottom:7px;color:var(--sub);font-size:12px;font-weight:800}.fmf-timezone-field>div{display:grid;grid-template-columns:22px minmax(0,1fr) 18px;align-items:center;gap:9px;min-height:52px;padding:0 14px;border:1px solid var(--border);border-radius:14px;background:var(--soft)}.fmf-timezone-field select{width:100%;appearance:none;border:0;outline:0;background:transparent;color:var(--text);font-size:14px;font-weight:750}.fmf-timezone-detect{min-height:46px;display:flex;align-items:center;justify-content:center;gap:8px;padding:10px 14px;border:1px solid rgba(118,55,238,.2);border-radius:13px;background:var(--psoft);color:var(--purple)!important;font-size:12px;font-weight:800;cursor:pointer}.fmf-timezone-help{display:flex;align-items:flex-start;gap:9px;padding:12px;border-radius:13px;background:var(--gsoft);color:var(--green)}.fmf-timezone-help span{color:var(--sub);font-size:12px;line-height:1.5}
        @media(max-width:1100px){.fmf-reading-guide{grid-template-columns:1fr}.fmf-timezone-action{max-width:none}}
        @media(max-width:900px){.fmf-modal-header{padding:20px}.fmf-modal-body{padding:20px}.fmf-event-row{min-height:106px}.fmf-event-reading{font-size:12px}}
        @media(max-width:620px){.fmf-actions{grid-template-columns:1fr 1fr}.fmf-timezone-action{grid-column:1/-1}.fmf-modal h2{font-size:22px}.fmf-modal-header p{font-size:12px}.fmf-modal-body{padding:16px}.fmf-reading-status,.fmf-reading-step{grid-template-columns:38px minmax(0,1fr);padding:12px}.fmf-reading-status>span,.fmf-reading-step>span{width:38px;height:38px}.fmf-timezone-preview{grid-template-columns:42px minmax(0,1fr)}.fmf-timezone-preview>span{width:42px;height:42px}.fmf-event-kpis{grid-template-columns:1fr 1fr}.fmf-event-kpis>div:last-child{grid-column:1/-1}}

      `}</style>

      <div className="fmf-page">
        <div className="fmf-shell">
          <header className="fmf-header">
            <div className="fmf-title">
              <span><BarChart3 /></span>
              <div><h1>Analyse fondamentale</h1><p>Contexte macro, risques, devises et actifs à surveiller en une seule vue.</p></div>
            </div>
            <button type="button" className="fmf-search-trigger" onClick={() => openModal("search")}><Search /><span>Rechercher un actif, une devise ou un événement...</span></button>
            <div className="fmf-actions">
              <button type="button" className="fmf-action fmf-timezone-action" onClick={() => openModal("timezone")} title="Modifier le fuseau horaire"><Globe2 /><span>{timeZoneLabel(timeZone)}</span></button>
              <button type="button" className="fmf-action" onClick={() => openModal("alerts")}><Bell />Alertes<span className="fmf-count">{alerts.length}</span></button>
              <button type="button" className="fmf-action is-primary" onClick={() => loadDashboard()} disabled={refreshing}><RefreshCw className={refreshing ? "fmf-spin" : ""} />{refreshing ? "Actualisation" : "Actualiser"}</button>
            </div>
          </header>

          {error ? <div className="fmf-error"><CloudOff /><div><strong>Données indisponibles</strong><span>{error}</span></div></div> : null}
          {data.warnings.length ? <div className="fmf-error"><AlertTriangle /><div><strong>Configuration à vérifier</strong><ul className="fmf-warning-list">{data.warnings.map((warning) => <li key={warning}>{warning}</li>)}</ul></div></div> : null}

          {!showInitialSkeleton && loading ? (
            <div className="fmf-background-loading" role="status" aria-live="polite">
              <span className="fmf-background-loading-dot" />
              <span>Actualisation des données en arrière-plan…</span>
            </div>
          ) : null}

          {!showInitialSkeleton && usingCachedData ? (
            <div className="fmf-cache-note">
              <Database />
              <span>Affichage instantané des dernières données enregistrées pendant la mise à jour.</span>
            </div>
          ) : null}

          {showInitialSkeleton ? (
            <FullMetrixLoader />
          ) : (
            <>
              <section className="fmf-summary">
                <button type="button" className="fmf-summary-card" onClick={() => openModal("insight")}>
                  <div className="fmf-summary-top"><IconBox icon={ShieldCheck} /><Direction value={String(overview.market_sentiment).toLowerCase().includes("off") ? "down" : "neutral"} /></div>
                  <span className="fmf-kicker">Sentiment du marché</span><strong>{overview.market_sentiment}</strong>
                  <div className="fmf-summary-foot"><span>Score de risque</span><b>{Math.round(overview.sentiment_score)}/100</b></div>
                </button>
                <button type="button" className="fmf-summary-card" onClick={() => strongest && openModal("currency", strongest)}>
                  <div className="fmf-summary-top"><IconBox icon={TrendingUp} tone="green" /><Direction value="up" /></div>
                  <span className="fmf-kicker">Devise forte</span><strong>{overview.strongest_currency}</strong>
                  <div className="fmf-summary-foot"><span>Score fondamental</span><b>{Math.round(overview.strongest_score)}/100</b></div>
                </button>
                <button type="button" className="fmf-summary-card" onClick={() => weakest && openModal("currency", weakest)}>
                  <div className="fmf-summary-top"><IconBox icon={TrendingDown} tone="red" /><Direction value="down" /></div>
                  <span className="fmf-kicker">Devise faible</span><strong>{overview.weakest_currency}</strong>
                  <div className="fmf-summary-foot"><span>Score fondamental</span><b>{Math.round(overview.weakest_score)}/100</b></div>
                </button>
                <button type="button" className="fmf-summary-card" onClick={() => openModal("calendar")}>
                  <div className="fmf-summary-top"><IconBox icon={CalendarDays} /><ArrowRight /></div>
                  <span className="fmf-kicker">Événement majeur</span><strong>{overview.major_event} · {formatTime(overview.major_event_date)}</strong>
                  <div className="fmf-summary-foot"><span>{overview.major_event_countdown}</span><Eye /></div>
                </button>
              </section>

              <section className="fmf-card fmf-quick">
                <div className="fmf-quick-main"><IconBox icon={Target} /><div><h2>Décision rapide</h2><p>{quick.global_bias}</p></div></div>
                <div className="fmf-quick-item"><span>Marchés à surveiller</span><strong>{quick.best_assets.join(" · ") || "Aucun signal fort"}</strong></div>
                <div className="fmf-quick-item"><span>À éviter maintenant</span><strong>{quick.avoid_assets.join(" · ") || "Aucun"}</strong></div>
                <div className="fmf-quick-item"><span>Prochain risque</span><strong>{quick.next_risk}{quick.next_risk_date ? ` · ${formatTime(quick.next_risk_date)}` : ""}</strong></div>
                <div className="fmf-quick-item"><span>Fenêtre de volatilité</span><strong>{formatVolatilityWindow(quick)}</strong></div>
              </section>

              <TraderReadingGuide overview={overview} quick={quick} strongest={strongest} weakest={weakest} />

              <section className="fmf-tools">
                <Comparator currencies={currencies} onOpen={(currency) => openModal("currency", currency)} />
                <div className="fmf-tools-stack">
                  <ImpactTool events={events} onOpen={(event) => openModal("event", event)} />
                  <RiskTool events={events} onOpen={(event) => openModal("event", event)} />
                </div>
              </section>

              <EngineStatus data={data} />

              <section className="fmf-main-grid">
                <article className="fmf-card fmf-panel">
                  <SectionHeader icon={CalendarDays} title="Événements économiques du jour" subtitle="Heures locales, impact et lecture immédiate pour le Forex." action={<button type="button" className="fmf-link" onClick={() => openModal("calendar")}>Voir tout <ArrowRight /></button>} />
                  <EventRows events={events} limit={5} onSelect={(event) => openModal("event", event)} />
                </article>
                <article className="fmf-card fmf-panel fmf-insight">
                  <SectionHeader icon={Sparkles} title="Insight IA FullMetrix" subtitle="Scénario macro expliqué simplement." action={<button type="button" className="fmf-link" onClick={() => openModal("insight")}>Détails <ArrowRight /></button>} />
                  <div className="fmf-insight-layout">
                    <div className="fmf-insight-copy">
                      <h3>{insight.title}</h3><p>{insight.summary}</p>
                      <div className="fmf-reasons">{insight.reasons.slice(0, 4).map((reason) => <div key={reason}><CheckCircle2 /><span>{reason}</span></div>)}</div>
                    </div>
                    <Ring value={insight.confidence} />
                  </div>
                  <div className="fmf-bias"><Sparkles /><span><strong>Biais global :</strong> {insight.global_bias}</span></div>
                </article>
              </section>

              <section className="fmf-secondary">
                <article className="fmf-card fmf-panel">
                  <SectionHeader icon={Gauge} title="Force des devises" subtitle="Classement fondamental sur les données récentes." />
                  {!currencies.length ? <EmptyState title="Aucun score de devise" /> : (
                    <div className="fmf-currencies">{currencies.slice(0, 6).map((currency, index) => {
                      const movement = Number(currency.change_24h) > 0 ? "up" : Number(currency.change_24h) < 0 ? "down" : "neutral";
                      const MoveIcon = movement === "up" ? ArrowUp : movement === "down" ? ArrowDown : Activity;
                      return <button type="button" className="fmf-currency" key={currency.currency} onClick={() => openModal("currency", currency)}><span>{index + 1}</span><CurrencyMark currency={currency.currency} className="fmf-ranking-mark" /><strong>{currency.currency}</strong><div className="fmf-track"><i style={{ width: `${currency.score}%` }} /></div><b>{Math.round(currency.score)}</b><span className={`fmf-change is-${movement}`}><MoveIcon />{currency.change_24h > 0 ? "+" : ""}{currency.change_24h}</span></button>;
                    })}</div>
                  )}
                </article>
                <article className="fmf-card fmf-panel">
                  <SectionHeader icon={BarChart3} title="Biais par marché" subtitle="Direction, confiance et lecture du différentiel macro." />
                  {!markets.length ? <EmptyState title="Aucun biais de marché" /> : (
                    <div className="fmf-markets">{markets.slice(0, 5).map((market) => <button type="button" className="fmf-market" key={market.symbol} onClick={() => openModal("market", market)}><header><h3>{market.symbol}</h3><ArrowRight /></header><Direction value={market.direction} /><Sparkline values={market.series} direction={normalizeDirection(market.direction)} /><footer><span>Confiance<strong>{Math.round(market.confidence)}%</strong></span><Eye /></footer></button>)}</div>
                  )}
                </article>
              </section>

              <section className="fmf-card fmf-panel">
                <SectionHeader icon={Bell} title="Alertes intelligentes" subtitle="Risques et opportunités à surveiller dans les prochaines heures." action={<button type="button" className="fmf-link" onClick={() => openModal("alerts")}>Voir toutes <ArrowRight /></button>} />
                {!alerts.length ? <EmptyState title="Aucune alerte active" /> : (
                  <div className="fmf-alert-grid">{alerts.slice(0, 3).map((alert) => <button type="button" className={`fmf-alert is-${alert.type}`} key={alert.id} onClick={() => openModal("alert", alert)}><span>{alert.type === "opportunity" ? <Target /> : alert.type === "critical" ? <Bell /> : <AlertTriangle />}</span><span><strong>{alert.title}</strong><small>{alert.description}</small></span><b>{alert.time}</b></button>)}</div>
                )}
              </section>
            </>
          )}
        </div>

        <Modal open={modal.type === "timezone"} onClose={closeModal} title="Fuseau horaire" subtitle="Affiche toutes les publications selon l’heure locale du trader." size="small">
          <TimeZoneSettings value={timeZone} onChange={updateTimeZone} />
        </Modal>

        <Modal open={modal.type === "search"} onClose={closeModal} title="Recherche fondamentale" subtitle="Actifs, devises et événements suivis par FullMetrix.">
          <label className="fmf-modal-search"><Search /><input autoFocus value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Exemple : EUR/USD, CPI, USD..." />{query ? <button type="button" onClick={() => setQuery("")}><X /></button> : null}</label>
          <div className="fmf-results">{!query ? <EmptyState title="Commence à écrire" text="Recherche un actif, une devise ou un événement." /> : searchResults.length ? searchResults.map((result, index) => <button type="button" key={`${result.type}-${index}`} onClick={() => { setQuery(""); openModal(result.type, result.payload); }}><IconBox icon={result.type === "event" ? CalendarDays : result.type === "currency" ? CircleDollarSign : BarChart3} tone={result.type === "event" ? "amber" : result.type === "currency" ? "green" : "purple"} /><span><strong>{result.title}</strong><small>{result.helper}</small></span><ArrowRight /></button>) : <EmptyState title="Aucun résultat" />}</div>
        </Modal>

        <Modal open={modal.type === "calendar"} onClose={closeModal} title="Calendrier économique" subtitle={`Toutes les publications converties en ${timeZoneLabel(timeZone)}.`} size="large"><EventRows events={events} onSelect={(event) => openModal("event", event)} /></Modal>

        <Modal open={modal.type === "insight"} onClose={closeModal} title="Insight IA FullMetrix" subtitle="Scénario central et facteurs principaux.">
          <div className="fmf-market-detail"><div><h3>{insight.title}</h3><p>{insight.summary}</p><div className="fmf-detail-list">{insight.reasons.map((reason) => <div key={reason}><CheckCircle2 />{reason}</div>)}</div></div><Ring value={insight.confidence} /></div>
          <div className="fmf-callout"><Sparkles /><span><strong>Biais global :</strong> {insight.global_bias}</span></div>
          {insight.disclaimer ? <div className="fmf-callout"><ShieldAlert /><span>{insight.disclaimer}</span></div> : null}
        </Modal>

        <Modal
          open={Boolean(selectedEvent)}
          onClose={closeModal}
          title={selectedEvent?.event || "Événement"}
          subtitle={`${selectedEvent?.currency || ""} · ${formatDateTime(selectedEvent?.datetime)}`}
          size="event"
        >
          <EventDetail event={selectedEvent} />
        </Modal>

        <Modal open={Boolean(selectedCurrency)} onClose={closeModal} title={`${selectedCurrency?.currency || "Devise"} — Analyse fondamentale`} subtitle={selectedCurrency?.summary}>
          <div className="fmf-modal-grid">
            <div className="fmf-stat"><span>Score global</span><strong>{Math.round(selectedCurrency?.score || 0)}/100</strong></div>
            <div className="fmf-stat"><span>Lecture</span><strong>{selectedCurrency?.stance || directionLabel(selectedCurrency?.trend)}</strong></div>
            <div className="fmf-stat"><span>Statut du score</span><strong>{scoreQualityLabel(selectedCurrency)}</strong></div>
            <div className="fmf-stat"><span>Confiance données</span><strong>{Math.round(selectedCurrency?.scoreConfidence || 0)}%</strong></div>
            <div className="fmf-stat"><span>Publications confirmées</span><strong>{selectedCurrency?.evidenceCount || 0}</strong></div>
            <div className="fmf-stat"><span>Facteurs analysés</span><strong>{selectedCurrency?.signalCount || 0}</strong></div>
          </div>
          <div className="fmf-callout"><Sparkles /><span><strong>Traduction simple :</strong> {selectedCurrency?.plainReading || selectedCurrency?.summary}</span></div>
          {!selectedCurrency?.evidenceCount ? (
            <div className="fmf-score-help"><ShieldAlert /><span><strong>Pourquoi 50/100 ?</strong> Aucune publication récente avec un résultat et une prévision comparables. Ce n'est pas un signal de trading.</span></div>
          ) : null}
          <div className="fmf-score-composition">
            <span><small>Base neutre</small><strong>50.0</strong></span>
            <span><small>Macro publié</small><strong>{Number(selectedCurrency?.scoreComponents?.macro_published || 0) >= 0 ? "+" : ""}{Number(selectedCurrency?.scoreComponents?.macro_published || 0).toFixed(1)}</strong></span>
            <span><small>Anticipations</small><strong>{Number(selectedCurrency?.scoreComponents?.market_expectations || 0) >= 0 ? "+" : ""}{Number(selectedCurrency?.scoreComponents?.market_expectations || 0).toFixed(1)}</strong></span>
            <span><small>Géopolitique</small><strong>{Number(selectedCurrency?.scoreComponents?.geopolitics || 0) >= 0 ? "+" : ""}{Number(selectedCurrency?.scoreComponents?.geopolitics || 0).toFixed(1)}</strong></span>
          </div>
          <section className="fmf-explanation-panel">
            <header><Sparkles /><div><small>Ce que le score veut réellement dire</small><strong>{selectedCurrency?.explanation?.headline || selectedCurrency?.plainReading}</strong></div></header>
            <p>{selectedCurrency?.explanation?.score_meaning || selectedCurrency?.methodology || "Le score synthétise les facteurs disponibles sans remplacer la lecture du prix."}</p>
            <div className="fmf-explanation-columns">
              <div><small>Direction</small><strong>{selectedCurrency?.explanation?.direction_meaning || selectedCurrency?.stance}</strong></div>
              <div><small>Confiance</small><strong>{selectedCurrency?.explanation?.confidence_meaning || `${Math.round(selectedCurrency?.scoreConfidence || 0)}% de couverture`}</strong></div>
              <div><small>À surveiller</small><strong>{selectedCurrency?.watchList?.[0] || "Les prochaines publications fortes et la réaction du prix."}</strong></div>
            </div>
          </section>
          {selectedCurrency?.driverDetails?.length ? (
            <>
              <h4 className="fmf-subtitle">Facteurs qui déplacent la devise</h4>
              <div className="fmf-driver-table">
                {selectedCurrency.driverDetails.slice(0, 8).map((driver, index) => (
                  <div key={`${driver.event}-${index}`} className={`is-${Number(driver.points || 0) > 0 ? "positive" : Number(driver.points || 0) < 0 ? "negative" : "neutral"}`}>
                    <span><small>{signalTypeLabel(driver.type)} · {driver.pillar_label || driver.pillar || "Macro"}</small><strong>{driver.event}</strong><em>{driver.explanation || driver.reading || "Facteur intégré dans le calcul."}</em></span>
                    <b>{Number(driver.points || 0) >= 0 ? "+" : ""}{Number(driver.points || 0).toFixed(2)}</b>
                  </div>
                ))}
              </div>
            </>
          ) : null}
          <h4 className="fmf-subtitle">Comment le score est construit</h4>
          <div className="fmf-breakdown">{Object.keys(selectedCurrency?.breakdown || {}).length ? Object.entries(selectedCurrency.breakdown).map(([label, value]) => <div key={label}><header><span>{label}</span><strong>{Math.round(Number(value) || 0)}</strong></header><div className="fmf-track"><i style={{ width: `${clamp(value)}%` }} /></div></div>) : <EmptyState title="Détail du score indisponible" />}</div>
        </Modal>

        <Modal open={Boolean(selectedMarket)} onClose={closeModal} title={`${selectedMarket?.symbol || "Marché"} — Biais fondamental`} subtitle={selectedMarket?.horizon}>
          <div className="fmf-market-detail">
            <div>
              <Direction value={selectedMarket?.direction} />
              <h3>{selectedMarket?.symbol}</h3>
              <p>{selectedMarket?.thesis}</p>
              <div className="fmf-detail-list">{selectedMarket?.drivers?.map((driver) => <div key={driver}><CheckCircle2 />{driver}</div>)}</div>
            </div>
            <Ring value={selectedMarket?.confidence || 0} label="Confiance" />
          </div>
          <section className="fmf-pair-explanation">
            <div>
              <small>Devise de base</small>
              <strong>{selectedMarket?.baseSnapshot?.currency || selectedMarket?.base || selectedMarket?.symbol?.split("/")?.[0] || "—"} · {Number(selectedMarket?.baseSnapshot?.score ?? selectedMarket?.baseScore ?? 50).toFixed(1)}/100</strong>
              <p>{selectedMarket?.baseSnapshot?.summary || "Force de la première devise de la paire."}</p>
            </div>
            <span><ArrowRight /></span>
            <div>
              <small>Devise de cotation</small>
              <strong>{selectedMarket?.quoteSnapshot?.currency || selectedMarket?.quote || selectedMarket?.symbol?.split("/")?.[1] || "—"} · {Number(selectedMarket?.quoteSnapshot?.score ?? selectedMarket?.quoteScore ?? 50).toFixed(1)}/100</strong>
              <p>{selectedMarket?.quoteSnapshot?.summary || "Force de la deuxième devise de la paire."}</p>
            </div>
          </section>
          <div className="fmf-callout"><Sparkles /><span><strong>Pourquoi ce biais :</strong> {selectedMarket?.explanation?.why_direction || selectedMarket?.thesis}</span></div>
          <div className="fmf-callout"><BarChart3 /><span><strong>Règle de la paire :</strong> {selectedMarket?.simpleRule}</span></div>
          <div className="fmf-callout"><Target /><span><strong>Action pratique :</strong> {selectedMarket?.traderAction}</span></div>
          <div className="fmf-callout"><ShieldAlert /><span><strong>Invalidation :</strong> {selectedMarket?.invalidation}</span></div>
        </Modal>

        <Modal open={modal.type === "alerts"} onClose={closeModal} title="Centre d'alertes" subtitle="Tous les risques et opportunités actifs.">
          <div className="fmf-alert-grid" style={{ gridTemplateColumns: "1fr" }}>{alerts.length ? alerts.map((alert) => <button type="button" className={`fmf-alert is-${alert.type}`} key={alert.id} onClick={() => openModal("alert", alert)}><span>{alert.type === "opportunity" ? <Target /> : alert.type === "critical" ? <Bell /> : <AlertTriangle />}</span><span><strong>{alert.title}</strong><small>{alert.description}</small></span><b>{alert.time}</b></button>) : <EmptyState title="Aucune alerte active" />}</div>
        </Modal>

        <Modal open={Boolean(selectedAlert)} onClose={closeModal} title={selectedAlert?.title || "Alerte"} subtitle={selectedAlert?.time} size="small">
          <div className="fmf-callout"><AlertTriangle /><span>{selectedAlert?.description}</span></div>
          <h4 className="fmf-subtitle">Actifs concernés</h4><div className="fmf-tags">{selectedAlert?.assets?.length ? selectedAlert.assets.map((asset) => <span key={asset}>{asset}</span>) : <span>Marchés globaux</span>}</div>
          <div className="fmf-callout"><Target /><span><strong>Action de prudence :</strong> {selectedAlert?.action}</span></div>
        </Modal>
      </div>
    </>
  );
}
